import React, { useState, useEffect, useRef } from "react";
import { DEFAULT_SCORING } from "../data/scoring";
import { POS_COLORS } from "../data/players";
import { buildRosterSlots } from "../engine/simulation";
import theme from "../data/theme";
import { fetchSleeperLeague, fetchSleeperUserLeagues, fetchLeagueAuctionHistory, fetchLeagueMembers } from "../utils/sleeperImport";
import { parseYahooSettings } from "../utils/yahooImport";
import { YAHOO_UNAVAILABLE_LEAD, YAHOO_UNAVAILABLE_BODY } from "../data/platformStatus";
import { fetchEspnLeagueHistory } from "../utils/espnImport";
import { anonymizeAuctionHistory } from "../utils/anonymizeAuctionHistory";
import { captureLeagueImportSaved, captureLeagueCreated } from "../utils/onboardingAnalytics";
import { createLeague, renameLeague, suggestLeagueName } from "../utils/leagueCreate";
import { captureSleeperLeagueCaptured, captureSleeperUserLookedUp, captureEspnDraftShape, buildEspnSeasonShape } from "../utils/recapAnalytics";

export const FORMAT_PRESETS = [
  {
    name: "Classic 10-Team",
    budget: 200, teams: 10,
    positions: { QB: 1, RB: 2, WR: 3, TE: 1, FLEX: 1, SFLEX: 0, DEF: 1, K: 1, BN: 6 },
    scoring: { ...DEFAULT_SCORING },
  },
  {
    // Real settings from a live Sleeper superflex auction. passYdsPer 25 is
    // Sleeper's pass_yd 0.04; the old object carried 30, which is not a value
    // any platform ships — and because 25 was absent, a genuine scoring rule
    // read as "already at default" and vanished from the analysis entirely.
    name: "Superflex 12-Team",
    budget: 200, teams: 12,
    positions: { QB: 1, RB: 2, WR: 3, TE: 1, FLEX: 2, SFLEX: 1, DEF: 0, K: 0, BN: 6 },
    scoring: { ...DEFAULT_SCORING, receptions: 1.0, passYdsPer: 25, passTD: 4, passInt: -1 },
  },
  {
    name: "Non-PPR 10-Team",
    budget: 200, teams: 10,
    positions: { QB: 1, RB: 2, WR: 3, TE: 1, FLEX: 1, SFLEX: 0, DEF: 1, K: 1, BN: 6 },
    scoring: { ...DEFAULT_SCORING, passYdsPer: 25, passTD: 4, passInt: -1, receptions: 0, fumbles: 0, fumblesLost: -2 },
  },
  {
    name: "Full-PPR 14-Team",
    budget: 200, teams: 14,
    positions: { QB: 1, RB: 2, WR: 3, TE: 1, FLEX: 3, SFLEX: 0, DEF: 0, K: 0, BN: 6 },
    scoring: { ...DEFAULT_SCORING, passYdsPer: 25, passTD: 4, passInt: -1, receptions: 1.0, fumbles: 0, fumblesLost: -2 },
  },
  {
    // FFPC: 1.0 PPR with TE receptions at 1.5 (recBonusTE 0.5 on top of the
    // base 1.0), 4pt pass TD, 0.05/pass yd (passYdsPer 20) and 0.1/rush+rec yd
    // (rush/recYdsPer 10) are the DEFAULT_SCORING values already.
    name: "FFPC TE-Premium 12-Team",
    budget: 200, teams: 12,
    positions: { QB: 1, RB: 2, WR: 2, TE: 1, FLEX: 2, SFLEX: 0, DEF: 1, K: 1, BN: 10 },
    scoring: { ...DEFAULT_SCORING, receptions: 1.0, recBonusTE: 0.5, passTD: 4, passInt: -1, fumblesLost: -1, fumbles: 0 },
  },
];

// Preset subtitle, DERIVED from the preset object at render time — never a
// hand-written string. A hand-written label is exactly how the stale Superflex
// preset stayed hidden for so long: the button read "Superflex 12-Team" while
// the object underneath said $240, 6pt pass TDs and a kicker. Deriving it means
// a wrong preset now announces itself on the button face.
export const presetSubtitle = (preset) => {
  const s = preset.scoring || {};
  const rec = s.receptions ?? 0;
  const base = rec === 0 ? "Non-PPR" : rec === 1 ? "Full PPR" : `${rec} PPR`;
  // TE premium rides WITH the PPR term, not at the end: it is a modifier on the
  // reception rate, and a trailing flag reads as an unrelated format note. FFPC
  // showing a bare "Full PPR" understated TE receptions by 50% — enough to
  // reprice the whole position — so the premium is spelled out, not just flagged.
  const tep = s.recBonusTE ?? 0;
  const ppr = tep > 0 ? `${base} +${tep} TEP` : base;
  const parts = [`${preset.teams} tm`, `$${preset.budget}`, ppr, `${s.passTD ?? 0}pt PTD`];
  if ((preset.positions?.SFLEX || 0) > 0) parts.push("SFLEX");
  return parts.join(" · ");
};

// Option-value token for the preset rows in the SAVED LEAGUES <select>. A
// <select> has one flat value space shared with the saved-league rows, so
// preset rows need a prefix — and the change handler resolves saved leagues
// FIRST, so a league that somehow carried this prefix in its name still wins.
// The token is never displayed; the option label is the preset name.
const PRESET_OPT = "__format_preset__";

// Long enough that holding the budget stepper is one write, short enough
// that closing the modal right after a change cannot beat it — the effect
// cleanup would cancel a pending save on unmount.
const AUTOSAVE_DEBOUNCE_MS = 400;

// ── CREATION IS A STEPPER ────────────────────────────────────────────
// Four steps, in the order someone actually answers them, and the last one
// ends the flow. v2 put "Done" at the bottom of the essentials with the rest
// of the setup hiding behind a "More options" link — which told the user they
// were finished while they were half set up. The options ARE the flow now, so
// that link is gone.
//
// Step 1 is the name overlay; 2-4 render inside the settings modal. Index 0 of
// this array is step 1, and `createStep` is 1-based to match the label the user
// reads ("Step 2 of 4"), so every lookup is CREATE_STEPS[createStep - 1].
//
// EDITING IS NOT THIS. A returning editor gets the tab bar — see the pencil
// path (startOnFormatTab). Only new leagues get walked.
const CREATE_STEPS = [
  { title: "Name", blurb: null },
  { title: "The essentials", blurb: "Set up the basics — everything saves as you go." },
  { title: "Scoring", blurb: "How your league scores. Standard PPR is already filled in — skip it if that's you." },
  { title: "Roster", blurb: "Starting lineup and bench. Also already filled in — Done is a fine answer." },
];
const CREATE_STEP_COUNT = CREATE_STEPS.length;

// ONE sentence for the mid-draft state, rendered at both places a user could
// be editing from. Two phrasings of one fact is how someone ends up believing
// the milder one.
const MID_DRAFT_UNSAVED_NOTICE =
  "A draft is in progress — changes here are NOT being saved. Finish or leave the draft first.";

// Scoring chips are shortcuts for ONE specific value each, not categories.
const SCORING_MODIFIERS = [
  { name: "TE Premium", key: "recBonusTE", onValue: 0.5, offValue: 0 },
  { name: "6PT Pass TD", key: "passTD", onValue: 6, offValue: 4 },
];

// Light the chips that an imported league's scoring already satisfies. Without
// this an imported TE-premium league showed the chip dark over correct 0.5
// scoring, which reads as "the import missed it".
//
// Exact match only: a league at 0.75 or 1.0 TEP keeps its raw value and stays
// chipless, because lighting the chip there would imply 0.5 and one toggle
// would silently overwrite their real number.
const deriveActiveModifiers = (scoring) =>
  SCORING_MODIFIERS.filter(m => (scoring?.[m.key] ?? null) === m.onValue).map(m => m.name);

// The exact dependency set of usePricing's dynamicPrices useMemo. The map the
// modal receives was built for the APPLIED settings; the analyzer runs on the
// UNAPPLIED draft. When these diverge (you clicked a preset but haven't hit
// Apply), the price map describes a different league than the VORP columns do —
// so we withhold it and the dollars columns hide themselves rather than showing
// last league's board as if it were this one.
export const priceMapMatchesDraft = (draft, applied) => {
  if (!applied) return false;
  if (draft.budget !== applied.budget || draft.teams !== applied.teams) return false;
  const posKeys = new Set([...Object.keys(draft.positions || {}), ...Object.keys(applied.positions || {})]);
  for (const k of posKeys) { if ((draft.positions?.[k] || 0) !== (applied.positions?.[k] || 0)) return false; }
  const scoreKeys = new Set([...Object.keys(draft.scoring || {}), ...Object.keys(applied.scoring || {})]);
  for (const k of scoreKeys) { if ((draft.scoring?.[k] ?? 0) !== (applied.scoring?.[k] ?? 0)) return false; }
  return true;
};

const settingsMatchPreset = (s, preset) => {
  if (s.budget !== preset.budget || s.teams !== preset.teams) return false;
  for (const k of Object.keys(preset.positions)) { if ((s.positions?.[k] || 0) !== (preset.positions[k] || 0)) return false; }
  for (const k of Object.keys(preset.scoring)) { if ((s.scoring?.[k] ?? 0) !== (preset.scoring[k] ?? 0)) return false; }
  return true;
};

// Reception label for the import summary. Deliberately NOT utils/pprLabel:
// that one says "Non-PPR" and has no half-PPR case, and it is compared against
// stored draft-value snapshots, so its strings can't be changed safely.
const importPprLabel = (rec) => {
  if (rec === 1) return "Full PPR";
  if (rec === 0.5) return "Half PPR";
  if (rec === 0) return "Standard";
  return `${rec} PPR`;
};

// One-line plain-language readback of what was just imported. Takes the
// COMPOSED settings object, never component state — `draft` lags a render
// behind setDraft, and showing a stale summary here is worse than none.
const importSummary = (s) => {
  const rosterSize = Object.values(s.positions || {}).reduce((a, b) => a + (b || 0), 0);
  const parts = [
    `${s.teams} teams`,
    `$${s.budget}`,
    importPprLabel(s.scoring?.receptions ?? 0),
    `${s.scoring?.passTD ?? 0}pt pass TD`,
    `${rosterSize}-man rosters`,
  ];
  if ((s.positions?.SFLEX || 0) > 0) parts.push("Superflex");
  if ((s.scoring?.recBonusTE || 0) > 0) parts.push("TE Premium");
  return parts.join(" · ");
};

// The one line the user is shown about what leaves the device on import.
// Rendered next to the Import controls, not on a policy page and not behind an
// expander — a disclosure the user has to go find is not a disclosure.
//
// Keep this sentence true to what anonymizeAuctionHistory actually emits. If
// that whitelist ever grows past prices and league shape, this line is wrong
// and has to change in the same commit.
const CONTRIBUTION_DISCLOSURE =
  "Anonymized auction prices are logged to improve the engine — never your league or manager names.";

// Contribute a freshly imported league's past auction results to the shared
// market dataset. Fire-and-forget by design:
//   - anonymizeAuctionHistory is the privacy boundary (a whitelist — see that
//     file). Nothing reaches the network that it did not construct by hand.
//   - null back means the league carries no history, and there is nothing to
//     contribute. Not an error, just a no-op.
//   - Every failure path is swallowed. A contribution is a side benefit of the
//     import; it must never turn a successful import into a failed one, and the
//     user is never shown a network error for it.
// No retry and no backfill: only imports from here on contribute, and a league
// already sitting in localStorage is never swept up.
function contributeAuctionHistory(league) {
  try {
    const payload = anonymizeAuctionHistory(league);
    if (!payload) return;
    fetch("/api/auction-history", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => { /* best-effort */ });
  } catch (e) { /* never fail an import on a contribution */ }
}

// ⚠️ ONE BRANCH PER ESPN ERROR, AND THEY DO NOT COLLAPSE. espnImport went to
// real trouble to distinguish these — 'players-unavailable' vs
// 'players-unmatched' exists precisely because one is worth retrying and the
// other is worth reporting — and flattening them into "Import failed" here,
// the only place the user actually reads them, would discard the taxonomy at
// the last step. Each answers "what do I do now?", so each carries its own
// `retryable`. Nothing here blames the user for something that is ours.
//
// Moved verbatim from the Almanac's EspnFlow (ac0c48c) when the ESPN import
// relocated to Settings; the copy is unchanged because the errors are.
function espnErrorCopy(result, hadCreds = false) {
  const { error, status, stoppedAt, startedAt } = result || {};

  // TWO DIFFERENT SITUATIONS BEHIND ONE STATUS. Without credentials, a 401 is
  // simply "this league is private" and the answer is to paste cookies. WITH
  // credentials it means the cookies are wrong or expired, and telling that
  // user their league is private would send them to re-read instructions they
  // already followed. `retryable` is true either way because the fix is the
  // same action — paste and go again — but the copy has to say which.
  if (error === "auth") {
    return hadCreds
      ? {
        title: "Those cookies didn't work",
        body: "ESPN rejected them. They expire — often within a few days — so the usual fix "
          + "is to copy both values again from a tab where you're signed in. Check that SWID "
          + "still has its { } braces and that neither value picked up a stray space.",
        retryable: true,
      }
      : {
        title: "This league is private",
        body: "ESPN only serves private leagues to a signed-in session. Paste the two cookies "
          + "below and try again — they're sent once to read your draft history and are never "
          + "stored.",
        retryable: true,
      };
  }

  if (error === "players-unavailable") {
    return {
      title: "ESPN's player service didn't answer",
      body: `Your league loaded fine — the separate call that turns player IDs into names `
        + `and positions failed${stoppedAt ? ` at ${stoppedAt}` : ""}. That's a temporary `
        + `problem on ESPN's side, not something wrong with your league. Trying again `
        + `usually works${stoppedAt ? `, and it picks up from ${stoppedAt} rather than starting over` : ""}.`,
      retryable: true,
    };
  }

  if (error === "players-unmatched") {
    return {
      title: "ESPN returned no players for this draft",
      body: "The player lookup worked but matched none of the IDs in your draft, so every "
        + "pick would have lost its position. Retrying won't change that — something is "
        + "genuinely off. Please send the league ID to mock1app@gmail.com and I'll look.",
      retryable: false,
    };
  }

  // Depth 0 means the walk never established the league exists at all. Deeper
  // 404s are just the end of history and never reach an error screen.
  if (status === 404 && stoppedAt != null && stoppedAt === startedAt) {
    return {
      title: "No league found for that ID",
      body: "ESPN has no league with that ID, or it isn't a football league. Check the "
        + "leagueId in your league's URL — it's the number after leagueId=.",
      retryable: false,
    };
  }

  if (error === "host") {
    return {
      title: "We couldn't reach ESPN correctly",
      body: "That's a bug on our end — the request went somewhere it shouldn't have. "
        + "Nothing is wrong with your league or your ID. It's worth telling me about: "
        + "mock1app@gmail.com.",
      retryable: true,
    };
  }

  if (error === "season-mismatch") {
    return {
      title: "ESPN returned the wrong season",
      body: "We asked for one year and ESPN answered with another, so the import was "
        + "stopped rather than saving history under the wrong year.",
      retryable: true,
    };
  }

  if (error === "Invalid league ID") {
    return {
      title: "That doesn't look like a league ID",
      body: "ESPN league IDs are digits only — the number after leagueId= in your league's URL.",
      retryable: false,
    };
  }

  return {
    title: "Import failed",
    body: String(error || "Something went wrong reaching ESPN."),
    retryable: true,
  };
}

// Which ESPN seasons become auction history, and a note when any do not.
//
// ⚠️ THE FILTER AND THE NOTE MUST NOT DRIFT, which is why they are computed
// together and this is the only place either exists. The summary step reads it
// to SHOW the note; handleEspnSave reads it to BUILD the record. Deriving the
// history in one place and the explanation in another is how a note ends up
// describing a filter that no longer matches.
//
// Without the note, a 5-season league with 2 auction seasons reads as "5
// seasons" on the summary and silently contributes prices from 2 — the same
// class of silent loss the dropped-slot and dropped-pick notes exist to
// prevent, so it gets the same treatment.
//
// TWO reasons a season is excluded, named separately because they mean
// different things:
//   snake     ESPN reports an auctionBudget on snake leagues too — that is its
//             DEFAULT, not a real setting (provenance §7.5) — but snake picks
//             carry no bid amounts. Including them would feed the shared market
//             dataset prices nobody ever paid.
//   no picks  An undrafted or empty season. Legitimate, not a failure, and
//             genuinely has nothing to contribute.
function buildEspnAuctionHistory(seasons) {
  const list = Array.isArray(seasons) ? seasons : [];
  const auction = list.filter(s => s.is_auction_draft && s.picks?.length);
  const snake = list.filter(s => !s.is_auction_draft);
  const empty = list.filter(s => s.is_auction_draft && !s.picks?.length);

  const auctionHistory = auction.map(s => ({
    season: String(s.season ?? ""), picks: s.picks,
  }));

  const excluded = snake.length + empty.length;
  let filteredNote = null;
  if (excluded > 0) {
    const reasons = [];
    if (snake.length) {
      reasons.push(
        `${snake.length} ${snake.length === 1 ? "was a snake draft" : "were snake drafts"} `
        + `(ESPN reports an auction budget on snake leagues by default, but their picks carry `
        + `no bid amounts, so including them would add prices nobody paid)`
      );
    }
    if (empty.length) {
      reasons.push(`${empty.length} had no picks yet`);
    }
    filteredNote =
      `Auction history: ${excluded} of ${list.length} season${list.length !== 1 ? "s" : ""} `
      + `contributed no prices — ${reasons.join(", and ")}. `
      + `League settings still come from the most recent season either way; only the `
      + `${auction.length} auction season${auction.length !== 1 ? "s" : ""} `
      + `${auction.length === 1 ? "carries" : "carry"} draft prices.`;
  }

  return { auctionHistory, filteredNote };
}

// stoppedBecause carries the walk's own vocabulary. 'max-depth' is the one that
// means "there may be more", and saying "end of history" there would be a lie.
function describeEspnStop(result) {
  const { stoppedAt, stoppedBecause } = result;
  if (stoppedBecause === "max-depth") {
    return "Stopped at the 20-season limit — this league may go back further.";
  }
  if (stoppedBecause === "players-unavailable") {
    return `Stopped at ${stoppedAt} — ESPN's player service didn't answer.`;
  }
  if (stoppedBecause === "auth") {
    return `Stopped at ${stoppedAt} — that season is private.`;
  }
  if (stoppedBecause === "season-mismatch") {
    return `Stopped at ${stoppedAt} — ESPN answered with a different year, which is how it reports "no season this far back".`;
  }
  if (stoppedAt != null) {
    return `Stopped at ${stoppedAt} — no season before that.`;
  }
  return "Walk completed.";
}

// The two ESPN session cookies, for private leagues.
//
// ⚠️ CONTROLLED BY THE PARENT'S STATE AND NOTHING ELSE. No localStorage, no
// defaultValue read from anywhere persistent, no autoComplete. type="password"
// is not about secrecy from the user — they just pasted these — it keeps the
// values out of shoulder-view and out of the browser's autofill store.
//
// THE INSTRUCTIONS ARE THE FEATURE. Nobody knows what espn_s2 is; a bare pair
// of boxes labelled with cookie names is unusable. The step list is the actual
// deliverable, and the SWID braces warning is the single most likely mistake —
// the value renders in DevTools with { } around it and they are PART OF THE
// VALUE, but they read like formatting, so people strip them.
function EspnCredentialFields({ s2, setS2, swid, setSwid, onHide }) {
  const field = {
    width: "100%", background: theme.colors.extended.modalBg,
    border: `1px solid ${theme.colors.border.default}`,
    borderRadius: 6, padding: "7px 10px", color: theme.colors.text.primary,
    fontSize: 12, fontFamily: theme.fonts.body,
  };
  const swidTrimmed = swid.trim();
  // Warn, never auto-correct. Silently re-adding braces would teach the user
  // their paste was right when the next one (somewhere else) will not be.
  const swidMissingBraces = swidTrimmed.length > 0
    && !(swidTrimmed.startsWith("{") && swidTrimmed.endsWith("}"));

  return (
    <div style={{
      marginTop: 8, padding: "10px 12px",
      background: theme.colors.extended.modalBg,
      border: `1px solid ${theme.colors.border.default}`,
      borderRadius: 6,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: theme.colors.text.secondary, letterSpacing: "0.5px" }}>
          PRIVATE LEAGUE — TWO COOKIES
        </span>
        <span onClick={onHide} style={{ fontSize: 10, color: theme.colors.text.faint, cursor: "pointer", textDecoration: "underline" }}>
          cancel
        </span>
      </div>

      <ol style={{ margin: "0 0 8px 0", paddingLeft: 16, fontSize: 10, color: theme.colors.text.secondary, lineHeight: 1.6 }}>
        <li>Open <code>fantasy.espn.com</code> in another tab, signed in.</li>
        <li>Press F12 (or ⌘⌥I) to open DevTools.</li>
        <li>Go to <strong>Application</strong> → <strong>Cookies</strong> → <code>https://fantasy.espn.com</code>.</li>
        <li>Find <code>espn_s2</code> and <code>SWID</code>, and copy each Value.</li>
      </ol>

      <div style={{ fontSize: 10, fontWeight: 600, color: theme.colors.text.secondary, marginBottom: 3 }}>espn_s2</div>
      <input
        type="password"
        value={s2}
        onChange={e => setS2(e.target.value)}
        placeholder="AEB…long string"
        autoComplete="off"
        spellCheck={false}
        style={field}
      />

      <div style={{ fontSize: 10, fontWeight: 600, color: theme.colors.text.secondary, margin: "8px 0 3px" }}>SWID</div>
      <input
        type="password"
        value={swid}
        onChange={e => setSwid(e.target.value)}
        placeholder="{XXXXXXXX-XXXX-…}"
        autoComplete="off"
        spellCheck={false}
        style={{
          ...field,
          borderColor: swidMissingBraces ? theme.colors.status.warning : theme.colors.border.default,
        }}
      />
      <div style={{
        fontSize: 10, marginTop: 3, lineHeight: 1.5,
        color: swidMissingBraces ? theme.colors.status.warning : theme.colors.text.faint,
      }}>
        {swidMissingBraces
          ? "Keep the { } braces — they're part of the value, not formatting. ESPN rejects SWID without them."
          : "Include the { } braces exactly as ESPN shows them."}
      </div>

      {/* The honest line, and it is now backed by a test: verifyEspnLeagueApi
          pushes sentinel credential values through every response the proxy can
          produce — including the 500 catch path — and asserts they appear
          nowhere but the outgoing Cookie header. */}
      <div style={{ marginTop: 8, fontSize: 10, lineHeight: 1.5, color: theme.colors.text.faint }}>
        Used once to read your draft history, then discarded. Never stored on our servers.
      </div>
    </div>
  );
}

// ── The essentials ───────────────────────────────────────────────────
// Budget, teams, QB format, and — for a named league — whether it's a keeper
// league. The four questions a new league actually has to answer.
//
// ONE renderer, TWO mount points: the guided essentials step a new league lands
// on, and the Format tab a returning editor opens. Shared rather than
// duplicated for the usual reason — two copies of a budget stepper drift, and
// the day they disagree is the day someone's league is one shape on one screen
// and another shape on the other.
//
// `onKnob` is how the caller learns a knob moved (the Format tab clears its
// active-preset highlight). The keeper row renders only when `onKeeperChange`
// is supplied, because the flag lives on a league RECORD and the DEFAULT
// pseudo-league has none to write it to.
function EssentialsControls({ draft, setDraft, onKnob, keeperValue = false, onKeeperChange = null }) {
  const bump = (delta) => {
    setDraft(prev => ({ ...prev, budget: Math.min(500, Math.max(50, prev.budget + delta)) }));
    onKnob?.();
  };
  const stepBtn = {
    width: 26, height: 26, borderRadius: 4, fontSize: 14, fontWeight: 700, fontFamily: "inherit",
    background: theme.colors.extended.modalBg, color: theme.colors.brand.orange,
    border: `1px solid ${theme.colors.border.default}`, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
  };
  const label = { fontSize: 11, fontWeight: 700, color: theme.colors.text.secondary, letterSpacing: "1px" };

  return (<>
    {/* Budget & Teams */}
    <div className="settings-budget-row" style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <label style={label}>BUDGET</label>
        <button className="btn" onClick={() => bump(-5)} style={stepBtn}>−</button>
        <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text.primary, minWidth: 36, textAlign: "center" }}>${draft.budget}</span>
        <button className="btn" onClick={() => bump(5)} style={stepBtn}>+</button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <label style={label}>TEAMS</label>
        {[8, 10, 12, 14, 16].map(n => (
          <button key={n} className="btn"
            onClick={() => { setDraft(prev => ({ ...prev, teams: n })); onKnob?.(); }}
            style={{
              padding: "4px 8px", borderRadius: 4, fontSize: 10, fontWeight: 600, fontFamily: "inherit",
              background: draft.teams === n ? theme.colors.brand.orange : theme.colors.extended.modalBg,
              color: draft.teams === n ? "#fff" : "#b0bec5",
              border: `1px solid ${theme.colors.border.default}`,
            }}>{n}</button>
        ))}
      </div>
    </div>

    {/* QB Format Toggle */}
    <div style={{ marginBottom: 16 }}>
      <span style={{ ...label, display: "block", marginBottom: 8 }}>QB FORMAT</span>
      <div style={{
        display: "flex", alignItems: "center", gap: 8, padding: "4px 8px",
        background: theme.colors.extended.modalBg, borderRadius: 6, border: `1px solid ${theme.colors.bg.elevated}`,
      }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: theme.colors.text.secondary, minWidth: 30 }}>QB</span>
        <div style={{ display: "flex", flex: 1, gap: 0 }}>
          {[
            { text: "Single QB", apply: { QB: 1, SFLEX: 0 }, match: () => draft.positions.SFLEX === 0 },
            { text: "Superflex", apply: { QB: 1, SFLEX: 1 }, match: () => draft.positions.SFLEX >= 1 },
          ].map((opt, i) => {
            const active = opt.match();
            return (
              <button key={opt.text} className="btn"
                onClick={() => { setDraft(prev => ({ ...prev, positions: { ...prev.positions, ...opt.apply } })); onKnob?.(); }}
                style={{
                  flex: 1, padding: "4px 6px", fontSize: 10, fontWeight: 600, fontFamily: "inherit",
                  background: active ? theme.colors.brand.orange : theme.colors.bg.elevated,
                  color: active ? "#fff" : "#b0bec5",
                  border: `1px solid ${theme.colors.border.default}`,
                  borderRadius: i === 0 ? "4px 0 0 4px" : "0 4px 4px 0",
                  borderLeft: i === 1 ? "none" : `1px solid ${theme.colors.border.default}`,
                  transition: "all 0.15s ease",
                }}
              >{opt.text}</button>
            );
          })}
        </div>
      </div>
    </div>

    {/* Keeper league? — the answer that decides where the Keepers section
        lives, asked once rather than discovered later. */}
    {onKeeperChange && (
      <div style={{ marginBottom: 16 }}>
        <span style={{ ...label, display: "block", marginBottom: 8 }}>KEEPER LEAGUE?</span>
        <div style={{
          display: "flex", alignItems: "center", gap: 8, padding: "4px 8px",
          background: theme.colors.extended.modalBg, borderRadius: 6, border: `1px solid ${theme.colors.bg.elevated}`,
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: theme.colors.text.secondary, minWidth: 30 }}>KEEP</span>
          <div style={{ display: "flex", flex: 1, gap: 0 }}>
            {[{ text: "No", val: false }, { text: "Yes", val: true }].map((opt, i) => {
              const active = keeperValue === opt.val;
              return (
                <button key={opt.text} className="btn" onClick={() => onKeeperChange(opt.val)}
                  style={{
                    flex: 1, padding: "4px 6px", fontSize: 10, fontWeight: 600, fontFamily: "inherit",
                    background: active ? theme.colors.brand.orange : theme.colors.bg.elevated,
                    color: active ? "#fff" : "#b0bec5",
                    border: `1px solid ${theme.colors.border.default}`,
                    borderRadius: i === 0 ? "4px 0 0 4px" : "0 4px 4px 0",
                    borderLeft: i === 1 ? "none" : `1px solid ${theme.colors.border.default}`,
                    transition: "all 0.15s ease",
                  }}
                >{opt.text}</button>
              );
            })}
          </div>
        </div>
        <div style={{ fontSize: 10, color: theme.colors.text.faint, marginTop: 5, lineHeight: 1.5 }}>
          Yes puts the Keepers section on the mock setup page, ready for you to fill in.
        </div>
      </div>
    )}
  </>);
}

// Colored chip row for a lineup. One renderer for both the Roster tab and the
// leagues/import tab so the two can't drift. `compact` only tightens spacing —
// the default rendering is byte-for-byte what the Roster tab had inline.
function RosterPreview({ positions, compact = false }) {
  const slots = buildRosterSlots(positions);
  const starters = slots.filter(s => s.pos !== "BN");
  const bench = slots.filter(s => s.pos === "BN");
  const chip = {
    fontSize: compact ? 8 : 9, fontWeight: 700,
    padding: compact ? "2px 6px" : "3px 8px",
    borderRadius: 4, letterSpacing: "0.5px",
  };
  return (
    <div style={{
      marginTop: compact ? 8 : 14, padding: compact ? "8px 10px" : "10px 12px",
      background: theme.colors.bg.deep, borderRadius: 8,
      border: `1px solid ${theme.colors.bg.elevated}`,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: theme.colors.text.faint, letterSpacing: "1px", marginBottom: compact ? 6 : 8 }}>
        LINEUP PREVIEW{compact && ` · ${slots.length} SLOTS`}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {starters.map((slot, i) => {
          const c = POS_COLORS[slot.pos] || POS_COLORS.BN;
          return (
            <span key={i} style={{ ...chip, background: c.bg, color: c.text }}>
              {slot.label.replace(/\d+/, '')}
            </span>
          );
        })}
        {bench.length > 0 && (
          <span style={{
            ...chip,
            background: (POS_COLORS.BN || { bg: theme.colors.bg.elevated }).bg,
            color: (POS_COLORS.BN || { text: theme.colors.text.muted }).text,
          }}>BN ×{bench.length}</span>
        )}
      </div>
    </div>
  );
}

// NOTE: the in-modal "done" state that used to live here was removed when
// imports started closing the modal. The modal is mounted conditionally, so it
// unmounts on close and a done-step could never render again. The confirmation
// now lives in the import toast on the underlying screen (see useLeagueSettings
// importNotice), which is what makes the import feel like it happened.

// mockDraftLive locks COMMITS, not the UI. Every tab and field stays viewable
// and editable in local `draft` state — looking up your scoring mid-draft is
// legitimate. What's blocked is anything that reaches committed state, because
// budget/teams/positions/scoring are dynamicPrices dependencies and feed
// calcAiMaxBidV2 plus the derived rosterSize: changing them mid-auction gives
// AI willingness and must-fill math that no longer match the rosters already
// drafted, with nothing shown to the user.
//
// Guarding only Apply would be insufficient — several paths write committed
// state BEFORE Apply and would slip past it. The full set is gated at the
// function level (not just the buttons), so a new caller can't reopen the hole:
//
//   handleApply         — the main commit (onApply + save-under-name)
//   loadLeague          — setActiveLeagueName before Apply
//   saveLeagueObject    — savedLeagues + localStorage + setActiveLeagueName
//   updateLeague        — same, and rewrites the ACTIVE league's record
//   deleteLeague        — same, and can null activeLeagueName
//   importYahooLeague   — saveLeagueObject + onApply
//   handleImportLeague  — Sleeper: saveLeagueObject + history + onApply
//   handleEspnSave      — ESPN: saveLeagueObject + history + onApply
//
// updateLeague/deleteLeague matter as much as the imports: activeLeague feeds
// leagueMultipliers, so rewriting or removing the active league's record moves
// dynamicPrices for the rest of the draft just as surely as editing budget.
const SettingsModal = React.memo(function SettingsModal({ settings, onApply, onClose, savedLeagues, setSavedLeagues, activeLeagueName, setActiveLeagueName, mockDraftLive = false, dynamicPrices = null, startInCreate = false, startOnFormatTab = false, onLiveChange = null, onFinishCreate = null }) {
  const [draft, setDraft] = useState({
    budget: settings.budget,
    teams: settings.teams,
    scoring: { ...settings.scoring },
    positions: { ...settings.positions },
  });

  const updatePos = (pos, val) => {
    const v = Math.max(0, Math.min(10, parseInt(val) || 0));
    setDraft(prev => ({ ...prev, positions: { ...prev.positions, [pos]: v } }));
    setActiveFormatPreset(null);
  };

  const totalSlots = Object.values(draft.positions).reduce((s, v) => s + v, 0);

  const posRows = [
    { pos: "QB", label: "Quarterback", color: "#dc2626" },
    { pos: "RB", label: "Running Back", color: "#059669" },
    { pos: "WR", label: "Wide Receiver", color: "#2563eb" },
    { pos: "TE", label: "Tight End", color: theme.colors.brand.deep },
    { pos: "FLEX", label: "Flex (RB/WR/TE)", color: "#7c3aed" },
    { pos: "SFLEX", label: "Superflex (QB/RB/WR/TE)", color: "#db2777" },
    { pos: "DEF", label: "Defense", color: theme.colors.text.faint },
    { pos: "K", label: "Kicker", color: theme.colors.text.faint },
    { pos: "BN", label: "Bench", color: theme.colors.text.muted },
  ];

  const inputStyle = {
    background: theme.colors.extended.modalBg, border: `1px solid ${theme.colors.border.default}`, borderRadius: 6,
    padding: "8px 12px", color: theme.colors.text.primary, fontSize: 14, fontFamily: theme.fonts.body,
    width: "100%", textAlign: "center",
  };

  const counterBtn = {
    background: theme.colors.bg.elevated, border: `1px solid ${theme.colors.border.default}`, borderRadius: 4,
    color: theme.colors.text.primary, width: 28, height: 28, fontSize: 16, fontWeight: 700,
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
    fontFamily: theme.fonts.body, transition: "all 0.1s ease", padding: 0,
    lineHeight: 1, boxSizing: "border-box", flexShrink: 0,
  };

  // The pencil and "Edit league settings" land on Format — the essentials
  // knobs, but inside the tab bar, because a returning editor came for a
  // specific thing and should not be walked through four steps to reach it.
  const [settingsTab, setSettingsTab] = useState(startOnFormatTab ? 1 : 0);
  const [scoringSubTab, setScoringSubTab] = useState(0);
  const [showCustomScoring, setShowCustomScoring] = useState(false);
  const [showCustomRoster, setShowCustomRoster] = useState(false);
  const [activePreset, setActivePreset] = useState(null);
  const [activeModifiers, setActiveModifiers] = useState(new Set());
  const [activeFormatPreset, setActiveFormatPreset] = useState(() => {
    const match = FORMAT_PRESETS.find(p => settingsMatchPreset(settings, p));
    return match ? match.name : null;
  });

  const [sleeperUsername, setSleeperUsername] = useState("");
  const [sleeperStep, setSleeperStep] = useState("username"); // 'username'|'picking'
  const [sleeperLeagues, setSleeperLeagues] = useState([]);
  const [sleeperLoading, setSleeperLoading] = useState(false);
  const [sleeperError, setSleeperError] = useState(null);
  const [sleeperUserId, setSleeperUserId] = useState(null);

  const handleFindLeagues = async () => {
    setSleeperLoading(true);
    setSleeperError(null);
    const { leagues, error, userId, season } = await fetchSleeperUserLeagues(sleeperUsername.trim());
    setSleeperLoading(false);
    if (error) { setSleeperError(error); return; }
    setSleeperLeagues(leagues || []);
    // The lookup already resolved this; holding it costs nothing and saves the
    // import a second /user round-trip to learn who is importing.
    setSleeperUserId(userId || null);
    captureSleeperUserLookedUp({ username: sleeperUsername.trim(), userId, season, leagues });
    setSleeperStep("picking");
  };

  const [sleeperImporting, setSleeperImporting] = useState(null); // league_id being imported

  // Yahoo OAuth import
  const [yahooStep, setYahooStep] = useState("idle"); // 'idle'|'waiting'|'picking'
  const [yahooLeagues, setYahooLeagues] = useState([]);
  const [yahooError, setYahooError] = useState(null);
  const yahooPopupRef = useRef(null);
  const yahooPollRef = useRef(null);
  const yahooHandledRef = useRef(false);

  // The one place a Yahoo league becomes the active league. Called by the
  // picker rows and, when the popup sends a single league, directly from the
  // message handler — so the parse/save/apply sequence exists exactly once.
  const importYahooLeague = (l) => {
    if (mockDraftLive) return; // locked mid-draft — see the header note
    try {
      const parsed = parseYahooSettings(l);
      // Compose once, then use THIS object everywhere — reading `draft` after
      // setDraft is stale.
      const importedSettings = {
        budget: parsed.budget,
        teams: parsed.teams,
        scoring: { ...DEFAULT_SCORING, ...parsed.scoring },
        positions: { ...parsed.positions },
      };
      const importedName = parsed.name || l.name || "Yahoo League";
      // Chips reflect the scoring that actually came in — see the Sleeper path.
      const derivedModifiers = deriveActiveModifiers(importedSettings.scoring);
      setDraft(importedSettings);
      setActiveModifiers(new Set(derivedModifiers));
      setActiveFormatPreset(null);
      // Commit immediately — see the Sleeper path.
      saveLeagueObject({ name: importedName, ...importedSettings, activePreset: null, activeModifiers: derivedModifiers });
      // Closes the modal and raises the confirmation toast on the screen behind
      // it — see the Sleeper path.
      onApply(importedSettings, { notice: { name: importedName, summary: importSummary(importedSettings) } });
      captureLeagueImportSaved("yahoo");
      setYahooError(null);
    } catch (e) {
      setYahooError("Failed to parse league settings: " + e.message);
      // Fall back to the picker so the failure is recoverable whether we got
      // here from a row click or straight off the popup message.
      setYahooStep("picking");
    }
  };

  // Intentionally uncalled while Yahoo API access is revoked — the "idle" step
  // renders the unavailable notice instead of the Connect button. Kept, not
  // deleted: this is the whole OAuth launch (and now the yahoo-error receiver),
  // so restoring access is re-wiring one onClick to it.
  // eslint-disable-next-line no-unused-vars
  const handleYahooImport = () => {
    setYahooStep("waiting");
    setYahooError(null);
    yahooHandledRef.current = false;
    const popup = window.open("/api/yahoo/auth", "yahoo-auth", "width=600,height=700");
    yahooPopupRef.current = popup;

    const onMessage = (e) => {
      // api/yahoo/callback.js posts yahoo-error when the OAuth is denied or the
      // Fantasy API refuses (403 since Jul 2026). Without a receiver the detail
      // only ever appeared inside the popup, and this modal sat on "Waiting for
      // Yahoo login..." until the poll below called it a cancellation — the
      // failure was real but arrived as the wrong story. Show it here instead.
      if (e.data?.type === "yahoo-error") {
        if (yahooHandledRef.current) return;
        yahooHandledRef.current = true;
        window.removeEventListener("message", onMessage);
        clearInterval(yahooPollRef.current);
        setYahooError(e.data.message || "Yahoo sign-in failed.");
        setYahooStep("idle");
        return;
      }
      if (e.data?.type !== "yahoo-leagues") return;
      // Import once per flow. The listener also unregisters itself, but a
      // duplicate message must never produce a second save.
      if (yahooHandledRef.current) return;
      yahooHandledRef.current = true;
      window.removeEventListener("message", onMessage);
      clearInterval(yahooPollRef.current);
      const leagues = e.data.leagues || [];
      setYahooLeagues(leagues);
      // api/yahoo/callback.js posts exactly the league whose "Import Settings"
      // button was clicked, so the picker would be a 1-item list asking the
      // user to make a choice they already made. Honor it. A multi-league
      // message (nothing sends one today) still gets the picker.
      if (leagues.length === 1) importYahooLeague(leagues[0]);
      else setYahooStep("picking");
    };
    window.addEventListener("message", onMessage);

    yahooPollRef.current = setInterval(() => {
      if (popup?.closed) {
        clearInterval(yahooPollRef.current);
        window.removeEventListener("message", onMessage);
        setYahooStep(prev => {
          if (prev === "waiting") {
            setYahooError("Login cancelled or window closed.");
            return "idle";
          }
          return prev;
        });
      }
    }, 500);
  };

  useEffect(() => {
    return () => {
      clearInterval(yahooPollRef.current);
    };
  }, []);

  const handleImportLeague = async (leagueId) => {
    if (mockDraftLive) return; // locked mid-draft — see the header note
    // Captured before any await — the input is still on screen behind the
    // picker and could be edited while the fetches are in flight.
    const importedBy = sleeperUsername.trim();
    const importedById = sleeperUserId;
    setSleeperImporting(leagueId);
    setSleeperError(null);
    const { settings: imported, leagueName: name, error, season, previousLeagueId } = await fetchSleeperLeague(leagueId);
    if (error) { setSleeperImporting(null); setSleeperError(error); return; }
    // Past auction results for this league, if it has any. Purely additive —
    // fetchLeagueAuctionHistory swallows its own errors and returns [], so a
    // league with no history (or an API hiccup) saves exactly as it does today.
    // Awaited before the save so the history rides along in one write; the
    // import spinner stays up for it.
    // Both extras ride along with the import and both swallow their own
    // errors, so neither can fail it. Parallel — they share no data.
    const [auctionHistory, members] = await Promise.all([
      fetchLeagueAuctionHistory(leagueId),
      fetchLeagueMembers(leagueId),
    ]);
    setSleeperImporting(null);
    // Compose once, then use THIS object for every step below. Reading `draft`
    // after setDraft would see the previous render's value.
    const importedSettings = {
      budget: imported.budget, teams: imported.teams,
      scoring: { ...imported.scoring }, positions: { ...imported.positions },
    };
    const importedName = name || "League";
    // Derive the chip state from what was imported. activePreset stays null —
    // platform scoring is not a preset the user picked — but the modifier
    // chips describe scoring values, so they must match what came in.
    const derivedModifiers = deriveActiveModifiers(importedSettings.scoring);
    setDraft(importedSettings);
    setActiveModifiers(new Set(derivedModifiers));
    setActiveFormatPreset(null);
    // Commit immediately. Requiring Save League + naming + Apply Settings after
    // an import is three steps nobody completed, so imports evaporated.
    // activePreset/activeModifiers are null here on purpose: this scoring came
    // from the platform, not from a preset the user picked.
    // saveLeagueObject REPLACES the record (filter().concat()), so anything the
    // object below omits is dropped. That's right for every data field — a
    // re-import exists to refresh them — but wrong for a setting the USER chose.
    // calibrate is the only such key on the record today (audited: name/budget/
    // teams/scoring/positions/auctionHistory/members/importedBy*/importedAt are
    // all import data, and activePreset/activeModifiers are derived from the
    // imported scoring). Carried only when it was explicitly set, so a first
    // import still defaults to off — and an explicit false is carried too,
    // rather than being re-derived as "unset".
    const existingLeague = savedLeagues.find(l => l.name === importedName);
    // Built as a named object rather than inline so the contribution POST below
    // anonymizes EXACTLY what was persisted. Re-deriving a second object there
    // would let the two drift, and the one that drifts is the one that leaves
    // the device.
    const leagueRecord = {
      name: importedName, ...importedSettings, activePreset: null, activeModifiers: [],
      ...(existingLeague?.calibrate !== undefined ? { calibrate: existingLeague.calibrate } : {}),
      // Omit the keys entirely when empty — absent means "not available", and
      // no downstream reader has to distinguish that from an empty array.
      ...(auctionHistory.length ? { auctionHistory } : {}),
      ...(members.length ? { members } : {}),
      // Which Sleeper account pulled this league in. Read by nothing yet —
      // it's what a "which manager are you?" picker needs to preselect the
      // user's own seat, and it can't be recovered after the fact.
      ...(importedBy ? { importedByUsername: importedBy } : {}),
      // Joins to members[].userId to identify the importer's own seat. Absent
      // when the league was reached without a Find Leagues lookup.
      ...(importedById ? { importedByUserId: importedById } : {}),
      // Stamps THIS import. MockSetup opens the opponents panel once per
      // import by comparing its seen-marker against this value, so a fresh
      // import reopens it even when the league name (and therefore the whole
      // configs blob, seen-marker included) is unchanged. Only on the Sleeper
      // path: it's the only import that carries members/history, and the panel
      // never auto-opens without those.
      importedAt: Date.now(),
    };
    saveLeagueObject(leagueRecord);
    contributeAuctionHistory(leagueRecord);
    // Close the modal and hand the confirmation to the screen behind it. Keeping
    // the modal open to show a success line read as "nothing happened" — users
    // kept clicking Import on an already-imported league.
    onApply(importedSettings, { notice: { name: importedName, summary: importSummary(importedSettings) } });
    captureLeagueImportSaved("sleeper");
    captureSleeperLeagueCaptured({ leagueId, leagueName: importedName, season, source: "settings_import", previousLeagueId });
  };

  // ── ESPN import (public and private leagues) ────────────────────────────
  //
  // Moved here from the Almanac path in ac0c48c. It belongs in Settings, not
  // the Almanac: ESPN's two calls fetch SETTINGS and DRAFT DETAIL only — there
  // are no matchups, so there was never an almanac to build. What it does
  // produce is exactly what this modal is for, a configured league plus its
  // auction history.
  //
  // 'input' → 'fetching' → 'summary' → save+close. The summary step is the one
  // thing the Sleeper path has no equivalent of, and it earns its place: the
  // walk reports startedAt / advancedFrom / stoppedBecause and the parsers emit
  // a notes array, none of which survives a handoff that saves immediately.
  const [espnStep, setEspnStep] = useState("input"); // 'input'|'fetching'|'summary'
  const [espnLeagueId, setEspnLeagueId] = useState("");
  const [espnError, setEspnError] = useState(null);
  const [espnProgress, setEspnProgress] = useState("");
  const [espnResult, setEspnResult] = useState(null);
  // Seasons already collected by a walk that stopped on something retryable. A
  // resume starts at stoppedAt and walks DEEPER, so these are strictly newer
  // and concatenate in front without overlapping.
  const [espnCarried, setEspnCarried] = useState([]);

  // ── PRIVATE LEAGUES: espn_s2 + SWID ─────────────────────────────────────
  //
  // ⚠️ THESE ARE SESSION COOKIES AND ARE TREATED AS SUCH. They live in
  // component state and NOWHERE else: never localStorage, never the saved
  // league record, never an analytics payload. The only place they may travel
  // is the outgoing request, where api/espn/league.js turns them into a Cookie
  // header — and verifyEspnLeagueApi pins that, scanning every response the
  // proxy can produce for sentinel credential values. That test is what makes
  // the line under the fields ("used once… never stored on our servers")
  // something we can actually claim.
  const [espnS2, setEspnS2] = useState("");
  const [espnSwid, setEspnSwid] = useState("");
  const [espnShowCreds, setEspnShowCreds] = useState(false);

  const clearEspnCreds = () => { setEspnS2(""); setEspnSwid(""); };

  // Belt and braces. SettingsModal is conditionally mounted today, so closing
  // it already destroys this state — this exists so the guarantee survives
  // someone later keeping the modal mounted and toggling visibility instead.
  useEffect(() => () => clearEspnCreds(), []);

  // BOTH OR NEITHER. A half pair reads to ESPN as a malformed session and 401s
  // a PUBLIC league that would otherwise have loaded anonymously.
  // api/espn/league.js enforces this server-side; the UI must not send a half
  // pair either, or a user who filled one box would be told their public league
  // is private. Returns {} — spread into the options, an empty object is
  // exactly "no credentials".
  const espnCredsPair = () => {
    const s2 = espnS2.trim();
    const swid = espnSwid.trim();
    return s2 && swid ? { espnS2: s2, swid } : {};
  };
  const espnHasCreds = () => !!(espnS2.trim() && espnSwid.trim());

  const runEspnWalk = async (startYear) => {
    setEspnStep("fetching");
    setEspnError(null);
    setEspnProgress("Looking up league...");

    // Captured before the await: the fields stay editable while the walk runs,
    // and the error copy below must describe the attempt that was actually
    // made, not whatever is in the boxes when it returns.
    const sentCreds = espnHasCreds();

    const res = await fetchEspnLeagueHistory(espnLeagueId.trim(), {
      ...(startYear != null ? { startYear } : {}),
      ...espnCredsPair(),
      onProgress: (message) => setEspnProgress(message),
    });

    // REVEAL ON THE AUTH ERROR rather than making the user self-identify.
    // Most people do not know whether their league is private — "private" is a
    // setting someone else may have chosen years ago — so the 401 is the first
    // reliable signal, and it is a normal outcome rather than an exceptional
    // one. A manual toggle exists too, for anyone who already knows.
    if (res.error === "auth") setEspnShowCreds(true);

    const merged = [...espnCarried, ...(res.seasons ?? [])];
    // Nothing collected on this pass or any before it — the only genuinely
    // fatal shape. Everything else has history worth showing.
    if (!merged.length) {
      setEspnResult({ ...res, sentCreds });
      setEspnError(res.error);
      setEspnStep("input");
      return;
    }
    // A 401 MID-WALK IS NORMAL, NOT EXCEPTIONAL — an ESPN session outlives a
    // short walk but not always a 20-season one. Handled exactly like
    // players-unavailable: the collected seasons are KEPT, the summary shows a
    // re-paste prompt, and Resume restarts at stoppedAt. Truncating here would
    // bake an expired cookie into a permanently short import.
    setEspnCarried(merged);
    setEspnResult({ ...res, seasons: merged, sentCreds });
    setEspnStep("summary");
  };

  const handleEspnImport = () => {
    if (!espnLeagueId.trim()) return;
    setEspnCarried([]);
    setEspnResult(null);
    runEspnWalk(null);
  };

  // stoppedAt is the year that FAILED, so a resume starts there rather than a
  // year later — that season is missing from the collected list and re-walking
  // it is the entire point.
  const handleEspnResume = () => runEspnWalk(espnResult?.stoppedAt ?? null);

  // The save. Mirrors handleImportLeague (Sleeper) step for step: compose the
  // settings object once, derive the chips from it, commit immediately, then
  // close via onApply with the confirmation notice.
  const handleEspnSave = () => {
    if (mockDraftLive) return; // locked mid-draft — see the header note
    const seasons = espnResult?.seasons ?? [];
    if (!seasons.length) return;

    // MOST RECENT season configures the league. `seasons` is newest-first and
    // the walk starts at the calendar year precisely so this is the CURRENT
    // settings — a league that changed format over the offseason would
    // otherwise be built to last year's roster with no signal it happened.
    const current = seasons[0];
    const importedSettings = {
      budget: current.budget, teams: current.teams,
      scoring: { ...current.scoring }, positions: { ...current.positions },
    };
    const importedName = current.name || "ESPN League";

    // Seasons' picks become the history in the same record shape the Sleeper
    // path produces: { season, picks }. No draftId — ESPN has no equivalent,
    // and it is not part of the shape the anonymizer reads. Auction seasons
    // only; the summary already told the user which were left out and why (see
    // buildEspnAuctionHistory — same call, same answer).
    const { auctionHistory } = buildEspnAuctionHistory(seasons);

    const derivedModifiers = deriveActiveModifiers(importedSettings.scoring);
    setDraft(importedSettings);
    setActiveModifiers(new Set(derivedModifiers));
    setActiveFormatPreset(null);

    // Same carry-forward rule as the Sleeper path: saveLeagueObject REPLACES
    // the record, so a setting the USER chose has to survive a re-import.
    const existingLeague = savedLeagues.find(l => l.name === importedName);
    // Named object so the contribution POST anonymizes EXACTLY what was
    // persisted — re-deriving a second object there would let the two drift,
    // and the one that drifts is the one that leaves the device.
    const leagueRecord = {
      name: importedName, ...importedSettings, activePreset: null, activeModifiers: [],
      ...(existingLeague?.calibrate !== undefined ? { calibrate: existingLeague.calibrate } : {}),
      ...(auctionHistory.length ? { auctionHistory } : {}),
      // No `members` and no `importedAt`. ESPN's league payload is narrowed to
      // settings + draftDetail by the proxy — it carries no owner list at all —
      // and importedAt exists to reopen MockSetup's opponents panel, which has
      // nothing to show without members.
      //
      // ⚠️ AND NO CREDENTIALS. espn_s2 / SWID are session cookies held in
      // component state; they are not spread in here, not read by
      // buildEspnAuctionHistory, and not present on any season object — the
      // proxy never echoes them back (verifyEspnLeagueApi pins that). This
      // record goes to localStorage via saveLeagueObject AND, anonymized, to
      // /api/auction-history, so a credential landing on it would be persisted
      // and transmitted at once. Do not add one here.
    };
    // The assertion behind the paragraph above. Cheap, and it fires in dev at
    // the moment a future edit introduces the leak rather than after a user's
    // cookies are already sitting in localStorage.
    //
    // Scoped to values that were ACTUALLY ELIGIBLE TO BE SENT (the complete
    // pair) and long enough that a substring hit means something. A one-
    // character field would otherwise match the "2" in a season year and throw
    // on a perfectly good public import — a false positive here breaks a real
    // import, so the check has to be narrower than "any non-empty input".
    // Real values are ~300 chars (espn_s2) and 38 (SWID); 8 is a safe floor.
    if (process.env.NODE_ENV !== "production") {
      const serialized = JSON.stringify(leagueRecord);
      const leaked = Object.values(espnCredsPair())
        .filter(v => v.length >= 8)
        .some(v => serialized.includes(v));
      if (leaked) {
        throw new Error("ESPN import: credential found in the saved league record — refusing to persist it.");
      }
    }
    saveLeagueObject(leagueRecord);
    contributeAuctionHistory(leagueRecord);

    // Instrumentation kept from ac0c48c, and it matters MORE here than it did
    // on the Almanac: this is the path that feeds the shared market dataset, so
    // the spend-vs-(budget × teams) reconciliation is the first real signal
    // about whether bidAmount is being parsed correctly at all. Shape only —
    // no names, no ids. Fired before onApply, which unmounts this component.
    const perSeason = buildEspnSeasonShape(seasons);
    captureEspnDraftShape({
      seasonCount: seasons.length,
      totalPicks: perSeason.reduce((n, s) => n + s.pick_count, 0),
      picksDropped: perSeason.reduce((n, s) => n + s.dropped_picks, 0),
      anyAuction: perSeason.some(s => s.is_auction),
      perSeason,
    });

    // DISCARD THE CREDENTIALS THE MOMENT THEY ARE DONE BEING USED — that is
    // what "used once to read your draft history, then discarded" means, and it
    // should be true before the modal closes rather than as a side effect of it
    // closing. The unmount cleanup is the backstop, not the mechanism.
    clearEspnCreds();

    // Close the modal and hand the confirmation to the screen behind it — see
    // the Sleeper path for why staying open read as "nothing happened".
    onApply(importedSettings, { notice: { name: importedName, summary: importSummary(importedSettings) } });
    captureLeagueImportSaved("espn");
  };

  const sleeperLeagueFormat = (l) => {
    const rec = l.scoring_settings?.rec ?? 0;
    const ppr = rec === 1 ? "Full PPR" : rec === 0.5 ? "Half PPR" : "Standard";
    const sf = (l.roster_positions || []).includes("SUPER_FLEX") ? "SF · " : "";
    return `${l.total_rosters}-team · ${sf}${ppr}`;
  };

  const [lastLoadedLeague, setLastLoadedLeague] = useState(activeLeagueName);
  const [updatedFlash, setUpdatedFlash] = useState(null);

  // Whether this league keeps players year to year. Lives on the league RECORD,
  // not in settings, because it is a fact about the league rather than an input
  // to pricing — and because shouldPromoteKeeperSection reads it off the record
  // to decide where the Keepers section lives.
  const [isKeeperLeague, setIsKeeperLeague] = useState(
    () => savedLeagues.find(l => l.name === activeLeagueName)?.isKeeperLeague === true
  );

  // ── The create stepper ───────────────────────────────────────────────
  // 0 = not creating. 1 = name, 2 = essentials, 3 = scoring, 4 = roster.
  //
  // 1-based so the state and the label the user reads ("Step 2 of 4") are the
  // same number — an off-by-one between those two is a bug nobody catches by
  // looking at the code, only by counting steps on screen.
  //
  // The name step still commits immediately: the league exists from step 1, so
  // X-ing out at step 3 loses nothing. What changed in v3 is what comes after
  // it — three more steps instead of one card and a link.
  const [createStep, setCreateStep] = useState(startInCreate ? 1 : 0);
  const [createName, setCreateName] = useState("");
  const [createNotice, setCreateNotice] = useState(null);
  // The league THIS run of the stepper created, or null on a fresh step 1.
  // It is what makes Back from step 2 a rename rather than a second create,
  // and it cannot be read off activeSaveName: someone who already had a league
  // loaded still has it loaded while they are naming the next one.
  const [createdName, setCreatedName] = useState(null);
  const importRef = useRef(null);

  const createOpen = createStep === 1;
  const inWizard = createStep >= 2;
  const stepMeta = CREATE_STEPS[Math.max(0, createStep - 1)];

  // `prefill` is how "Save a copy" seeds a forked name. One naming UI for
  // every path that needs a name — create, fork, and promoting DEFAULT — so
  // the collision guard and the suffix suggestion cover all three rather than
  // only the one they were written for.
  const openCreate = (prefill = "") => {
    setCreateName(prefill);
    setCreateNotice(null);
    setCreatedName(null);
    setCreateStep(1);
  };

  // The secondary path on the same step. Imports already name and save
  // themselves, so this hands off rather than duplicating anything: close the
  // step, go to My Leagues, and put the import blocks under the user's nose.
  const goToImport = () => {
    setCreateStep(0);
    setCreateNotice(null);
    setSettingsTab(0);
    requestAnimationFrame(() => importRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }));
  };

  // Check if current draft settings exactly match a saved league
  const leagueMatchesSettings = (league) => {
    if (league.budget !== draft.budget || league.teams !== draft.teams) return false;
    const posKeys = Object.keys(draft.positions);
    for (const k of posKeys) { if ((league.positions[k] || 0) !== (draft.positions[k] || 0)) return false; }
    const scoringKeys = Object.keys(draft.scoring);
    for (const k of scoringKeys) { if ((league.scoring[k] || 0) !== (draft.scoring[k] || 0)) return false; }
    return true;
  };
  const matchedLeague = savedLeagues.find(l => leagueMatchesSettings(l));
  const hasUnsavedChanges = !matchedLeague;

  const loadLeague = (league) => {
    if (mockDraftLive) return; // locked mid-draft — see the header note
    setDraft({ budget: league.budget, teams: league.teams, scoring: { ...league.scoring }, positions: { ...league.positions } });
    setActivePreset(league.activePreset || null);
    setActiveModifiers(new Set(league.activeModifiers || []));
    setActiveLeagueName(league.name);
    setLastLoadedLeague(league.name);
    setIsKeeperLeague(league.isKeeperLeague === true);
    const match = FORMAT_PRESETS.find(p => settingsMatchPreset(league, p));
    setActiveFormatPreset(match ? match.name : null);
  };

  // A format preset picked from the SAVED LEAGUES dropdown. Same effect the
  // Format tab's preset buttons used to have — the buttons moved to the two
  // league selectors, the behaviour did not.
  //
  // It names the config (so the chip stops saying DEFAULT and the header and
  // this modal agree about what you picked) but deliberately does NOT persist
  // it: the preset name is not in savedLeagues, so activeSaveName stays null,
  // Apply stays a pure apply, and "Save as New" is how a preset gets promoted
  // to a real league. That is exactly what picking one from the header
  // dropdown already did.
  const applyFormatPreset = (preset) => {
    if (mockDraftLive) return; // locked mid-draft — see the header note
    setDraft({ budget: preset.budget, teams: preset.teams, positions: { ...preset.positions }, scoring: { ...preset.scoring } });
    setActivePreset(null);
    setActiveModifiers(new Set());
    setActiveFormatPreset(preset.name);
    setActiveLeagueName(preset.name);
    setLastLoadedLeague(preset.name);
    setIsKeeperLeague(false);
  };

  // Persist a fully-formed league object and make it the active one.
  // Overwrite-on-same-name is the existing convention. Shared by the manual
  // Save League button and both platform imports so they cannot drift.
  const saveLeagueObject = (league) => {
    if (mockDraftLive) return; // locked mid-draft — see the header note
    const updated = savedLeagues.filter(l => l.name !== league.name).concat(league);
    setSavedLeagues(updated);
    localStorage.setItem("auctionArchitect_savedLeagues", JSON.stringify(updated));
    setActiveLeagueName(league.name);
    setLastLoadedLeague(league.name);
  };

  // Create: validate the name, then commit through saveLeagueObject.
  //
  // ATOMICITY IS THE POINT. saveLeagueObject is the single primitive that
  // writes savedLeagues + localStorage AND sets the league current, so there is
  // no ordering to get wrong and no window in which the league exists but the
  // user is still on DEFAULT. A league created but not current is exactly the
  // failure this flow was built to end — the user names their league, sees the
  // chip still say DEFAULT, and concludes nothing happened.
  //
  // Landing on Format (not staying here) is the other half: the league exists,
  // so the next question really is what shape it is.
  //
  // ONE record builder for every writer — create, fork, update, auto-save.
  // isKeeperLeague only reaches storage because it is in here; adding a field
  // to one writer and not the others is how a league ends up with a flag that
  // survives a fork but not an edit.
  const draftRecordFields = () => ({
    scoring: { ...draft.scoring }, positions: { ...draft.positions },
    budget: draft.budget, teams: draft.teams,
    activePreset, activeModifiers: [...activeModifiers],
    isKeeperLeague,
  });

  // Rename the record in place, keeping every other field. Only ever called
  // from the name step ON A REVISIT — see renameLeague in utils/leagueCreate
  // for why that scope is what makes not migrating the per-league stores safe.
  const renameActiveLeague = (from, to) => {
    if (mockDraftLive) return; // locked mid-draft — see the header note
    const updated = savedLeagues.map(l => (l.name === from ? { ...l, name: to } : l));
    setSavedLeagues(updated);
    localStorage.setItem("auctionArchitect_savedLeagues", JSON.stringify(updated));
    setActiveLeagueName(to);
    setLastLoadedLeague(to);
  };

  // Step 1's primary button. Two jobs, and which one fires depends on whether
  // this run of the stepper has already created its league:
  //   fresh    — validate and CREATE, then walk on to step 2.
  //   revisit  — they pressed Back from step 2. The league exists, so this is
  //              rename-or-advance. Creating a second one would be the obvious
  //              wrong answer and the easy bug.
  const handleNameStepNext = () => {
    if (mockDraftLive) return; // locked mid-draft — see the header note

    if (createdName) {
      const res = renameLeague(savedLeagues, createdName, createName);
      if (!res.ok) {
        setCreateNotice(res.message);
        if (res.reason === "collision") setCreateName(res.suggestion);
        return;
      }
      if (res.renamed) { renameActiveLeague(createdName, res.name); setCreatedName(res.name); }
      setCreateNotice(null);
      setCreateStep(2);
      return;
    }

    const res = createLeague(savedLeagues, createName, draftRecordFields());
    if (!res.ok) {
      setCreateNotice(res.message);
      // Offer the suffix by filling it in — the user still has to press
      // Next, so nothing is renamed or overwritten behind their back.
      if (res.reason === "collision") setCreateName(res.suggestion);
      return;
    }
    saveLeagueObject(res.league);
    // The draft becomes the LIVE settings too. Without this, promoting a
    // modified DEFAULT config ("Save as a league…") would store the new shape
    // and leave the app pricing off the old one — and auto-save could not fix
    // it afterwards, because the record it would compare against is the one
    // just written from this very draft.
    onLiveChange?.(draft);
    setCreatedName(res.league.name);
    setCreateNotice(null);
    setCreateStep(2); // on into the stepper — see CREATE_STEPS
    captureLeagueCreated("settings_modal");
  };

  // Back from 2 lands on the name step, so it has to carry the name that is
  // actually saved — an empty box there would read as "type it again".
  const goBackStep = () => {
    if (createStep === 2) { setCreateName(createdName || ""); setCreateNotice(null); }
    setCreateStep(s => Math.max(1, s - 1));
  };

  // Cancel on step 1 means two different things too. On a revisit the league
  // exists and they were only looking at its name, so it returns them to the
  // step they came from rather than dumping them out of a flow they are
  // halfway through.
  const cancelNameStep = () => {
    setCreateNotice(null);
    setCreateStep(createdName ? 2 : 0);
  };

  // The end of the flow. Everything is already saved — auto-save has been
  // writing since step 1 — so this only has to leave: end the stepper and hand
  // the user to their league. onFinishCreate is what puts them on the mock
  // setup page; without it Done would close onto whatever screen they happened
  // to open the modal from.
  const finishCreate = () => {
    setCreateStep(0);
    setCreatedName(null);
    (onFinishCreate || onClose)();
  };

  const deleteLeague = (name) => {
    if (mockDraftLive) return; // locked mid-draft — see the header note
    if (savedLeagues.length <= 1) return;
    const updated = savedLeagues.filter(l => l.name !== name);
    setSavedLeagues(updated);
    localStorage.setItem("auctionArchitect_savedLeagues", JSON.stringify(updated));
    if (activeLeagueName === name) setActiveLeagueName(null);
    if (lastLoadedLeague === name) setLastLoadedLeague(null);
  };

  const updateLeague = (name) => {
    if (mockDraftLive) return; // locked mid-draft — see the header note
    const updated = savedLeagues.map(l => l.name === name ? { ...l, ...draftRecordFields() } : l);
    setSavedLeagues(updated);
    localStorage.setItem("auctionArchitect_savedLeagues", JSON.stringify(updated));
    setActiveLeagueName(name);
    setLastLoadedLeague(name);
    setUpdatedFlash(name);
    setTimeout(() => setUpdatedFlash(null), 1200);
  };

  // The name Apply should save under: the loaded league, and only if it still
  // exists — it may have been deleted. null means "no target", and Apply stays
  // a pure apply, which is the DEFAULT pseudo-league's behaviour.
  //
  // The create step no longer needs a second source here. It commits through
  // saveLeagueObject, which sets lastLoadedLeague as part of the same call, so
  // a freshly created league is already the save target by the time the user
  // lands on Format. The old `buildingName ||` branch existed only because the
  // build-by-hand path deferred the write; nothing defers it now.
  const activeSaveName = lastLoadedLeague && savedLeagues.some(l => l.name === lastLoadedLeague)
    ? lastLoadedLeague : null;

  // ── AUTO-SAVE: a named league is LIVE ────────────────────────────────
  // Every change writes itself. No Apply, no "unsaved changes", no way to
  // edit your league for five minutes and lose it by closing the modal.
  //
  // TWO writes, and both are needed or the feature is a lie:
  //   updateLeague  — the RECORD in savedLeagues + localStorage. Still the
  //                   only writer; auto-save adds a caller, not a code path.
  //   onLiveChange  — the LIVE settings the rest of the app prices off. Skip
  //                   this and the user changes budget to $300, sees "Saved",
  //                   and drafts against a $200 board.
  //
  // ────────────────────────────────────────────────────────────────────
  // THE MID-DRAFT GUARD. This is the one place v2 could corrupt a draft, and
  // the answer is: while `mockDraftLive`, auto-save does not run. At all.
  //
  // Not a precaution — a live auction READS `settings` continuously. Verified
  // in useMockDraft: `rosterSize` is derived from settings.positions on every
  // render; the nominator rotation walks `% settings.teams`; the tier anchor
  // scales by `settings.budget`; and `settings` is passed straight into
  // calcAiMaxBidV2 and pickAiNomination on every single bid. Only the TEAMS
  // are snapshotted (buildDraftTeams runs once at start), which is the worst
  // possible pairing: rosters frozen under the old budget, math live on the
  // new one. useMockDraft's own comment at the sold-handler deps says it out
  // loud — "identical in-draft — neither changes mid-auction". Auto-save is
  // exactly the thing that would make that comment false.
  //
  // Belt and braces: this effect returns early, AND updateLeague /
  // saveLeagueObject keep their own mockDraftLive guards from v1, so a future
  // caller cannot reopen the hole by forgetting this one.
  //
  // The user is told, because silence is worse than the old disabled Apply:
  // the actions row says changes are not being saved while a draft is live.
  // ────────────────────────────────────────────────────────────────────
  //
  // Debounced, and it stops on its own: once the write lands, savedLeagues
  // holds exactly what the draft holds, `recordDiffers` goes false and the
  // effect no-ops. That same comparison is what keeps it from firing a
  // pointless save the moment the modal opens.
  const recordDiffers = (saved, rec) => {
    if (!saved) return false;
    if (saved.budget !== rec.budget || saved.teams !== rec.teams) return true;
    if ((saved.isKeeperLeague === true) !== rec.isKeeperLeague) return true;
    if ((saved.activePreset || null) !== (rec.activePreset || null)) return true;
    const savedMods = [...(saved.activeModifiers || [])].sort().join("|");
    if (savedMods !== [...rec.activeModifiers].sort().join("|")) return true;
    for (const k of new Set([...Object.keys(saved.positions || {}), ...Object.keys(rec.positions)])) {
      if ((saved.positions?.[k] || 0) !== (rec.positions[k] || 0)) return true;
    }
    for (const k of new Set([...Object.keys(saved.scoring || {}), ...Object.keys(rec.scoring)])) {
      if ((saved.scoring?.[k] ?? 0) !== (rec.scoring[k] ?? 0)) return true;
    }
    return false;
  };

  useEffect(() => {
    if (!activeSaveName) return;   // DEFAULT stays commit-based, by design
    if (mockDraftLive) return;     // THE GUARD — see above
    const saved = savedLeagues.find(l => l.name === activeSaveName);
    if (!recordDiffers(saved, draftRecordFields())) return;
    const t = setTimeout(() => {
      updateLeague(activeSaveName);
      onLiveChange?.(draft);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSaveName, mockDraftLive, savedLeagues, draft, isKeeperLeague, activePreset, activeModifiers]);

  // The DEFAULT pseudo-league's commit, unchanged: quick-play settings are
  // applied on demand and belong to nobody until the user names them.
  const handleApply = () => {
    if (mockDraftLive) return; // locked mid-draft — see the header note
    onApply(draft);
  };

  const basePresets = [
    { name: "Sleeper Default", changes: { receptions: 1, passTD: 4, passInt: -1 } },
    { name: "Yahoo Default", changes: { receptions: 0.5, passTD: 4, passInt: -1 } },
    { name: "ESPN Default", changes: { receptions: 1, passTD: 4, passInt: -2 } },
  ];

  const scoringModifiers = SCORING_MODIFIERS;

  const updateScoring = (key, val) => {
    setDraft(prev => ({ ...prev, scoring: { ...prev.scoring, [key]: val } }));
    setActiveFormatPreset(null);
  };

  const applyBasePreset = (preset) => {
    setActivePreset(preset.name);
    setActiveFormatPreset(null);
    const modOverrides = {};
    activeModifiers.forEach(modName => {
      const mod = scoringModifiers.find(m => m.name === modName);
      if (mod) modOverrides[mod.key] = mod.onValue;
    });
    setDraft(prev => ({ ...prev, scoring: { ...DEFAULT_SCORING, ...preset.changes, ...modOverrides } }));
  };

  const toggleModifier = (mod) => {
    setActiveFormatPreset(null);
    setActiveModifiers(prev => {
      const next = new Set(prev);
      const isActive = next.has(mod.name);
      if (isActive) next.delete(mod.name); else next.add(mod.name);
      setDraft(d => ({ ...d, scoring: { ...d.scoring, [mod.key]: isActive ? mod.offValue : mod.onValue } }));
      return next;
    });
  };

  const scoringInput = (key, label, step = 1) => (
    <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "3px 0" }}>
      <span style={{ fontSize: 11, color: theme.colors.text.secondary }}>{label}</span>
      <input type="number" step={step} value={draft.scoring[key]} onChange={e => updateScoring(key, parseFloat(e.target.value) || 0)}
        style={{ width: 56, background: theme.colors.extended.modalBg, border: `1px solid ${theme.colors.border.default}`, borderRadius: 4, padding: "3px 6px", color: theme.colors.text.primary, fontSize: 12, textAlign: "center", fontFamily: theme.fonts.body }} />
    </div>
  );

  const bonusRows = [
    { label: "Pass", color: "#dc2626", threshKey: "passYdsBonusThreshold", ptsKey: "passYdsBonus", defaultThresh: 300, threshStep: 25, defaultPts: 5 },
    { label: "Rush", color: "#059669", threshKey: "rushYdsBonusThreshold", ptsKey: "rushYdsBonus", defaultThresh: 100, threshStep: 10, defaultPts: 5 },
    { label: "Rec", color: "#2563eb", threshKey: "recYdsBonusThreshold", ptsKey: "recYdsBonus", defaultThresh: 100, threshStep: 10, defaultPts: 5 },
  ];

  const scoringGroups = [
    { title: "PASSING", color: "#dc2626", fields: [
      ["passYdsPer", "Yards per Point", 1], ["passTD", "Touchdown", 1], ["passInt", "Interception", 1], ["passComp", "Completion", 0.1],
    ]},
    { title: "RUSHING", color: "#059669", fields: [
      ["rushYdsPer", "Yards per Point", 1], ["rushTD", "Touchdown", 1],
    ]},
    { title: "RECEIVING", color: "#2563eb", fields: [
      ["receptions", "Reception", 0.5], ["recBonusRB", "RB Rec Bonus", 0.25], ["recBonusWR", "WR Rec Bonus", 0.25], ["recBonusTE", "TE Premium", 0.25],
      ["recYdsPer", "Yards per Point", 1], ["recTD", "Touchdown", 1],
    ]},
    { title: "MISC", color: theme.colors.brand.orange, fields: [
      ["fumbles", "Fumble", 1], ["fumblesLost", "Fumble Lost", 1], ["twoPtConv", "2-Pt Conv", 1], ["returnTD", "Return TD", 1],
    ]},
    { title: "KICKER", color: theme.colors.brand.deep, fields: [
      ["fgMade0_19", "FG 0-19", 1], ["fgMade20_29", "FG 20-29", 1], ["fgMade30_39", "FG 30-39", 1],
      ["fgMade40_49", "FG 40-49", 1], ["fgMade50", "FG 50+", 1], ["fgMissed", "FG Missed", 1], ["patMade", "PAT Made", 1], ["patMissed", "PAT Missed", 1],
    ]},
    { title: "DEFENSE", color: theme.colors.text.faint, fields: [
      ["defSack", "Sack", 1], ["defInt", "Interception", 1], ["defFumRec", "Fumble Rec", 1],
      ["defTD", "Touchdown", 1], ["defSafety", "Safety", 1], ["defBlockKick", "Blocked Kick", 1],
    ]},
    { title: "DEF PTS ALLOWED", color: theme.colors.text.faint, fields: [
      ["defPtsAllowed0", "0 Pts", 1], ["defPtsAllowed1_6", "1-6 Pts", 1], ["defPtsAllowed7_13", "7-13 Pts", 1],
      ["defPtsAllowed14_20", "14-20 Pts", 1], ["defPtsAllowed21_27", "21-27 Pts", 1],
      ["defPtsAllowed28_34", "28-34 Pts", 1], ["defPtsAllowed35", "35+ Pts", 1],
    ]},
  ];

  // ── The Scoring and Roster tab bodies, as ONE definition each ────────
  //
  // Two mount points apiece: the tab a returning editor clicks, and step 3 /
  // step 4 of the create stepper. Render FUNCTIONS rather than elements so the
  // tree is still built only for the panel actually on screen — and so there is
  // exactly one copy of each. A second copy of the scoring grid is a second
  // place a new scoring key has to be added, and the day they disagree is the
  // day a league scores one way in the wizard and another way in the tabs.
  const renderScoringTab = () => (<>
    <div className="sm-scoring" style={{ marginBottom: 20 }}>
      {/* Current scoring summary */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: theme.colors.text.secondary, letterSpacing: "1px" }}>SCORING</span>
        <span style={{ fontSize: 11, color: theme.colors.brand.orange, fontWeight: 600 }}>
          {draft.scoring.receptions === 0 ? "Standard" : draft.scoring.receptions === 0.5 ? "Half-PPR" : draft.scoring.receptions === 1 ? "PPR" : `${draft.scoring.receptions} PPR`}
          {" · "}{draft.scoring.passTD}pt Pass TD
        </span>
      </div>

      {/* PPR Quick Toggle */}
      <div style={{ display: "flex", gap: 0, marginBottom: 10 }}>
        {[
          { label: "Standard", value: 0 },
          { label: "Half PPR", value: 0.5 },
          { label: "Full PPR", value: 1 },
        ].map((opt, i) => {
          const active = draft.scoring.receptions === opt.value;
          return (
            <button key={opt.label} className="btn"
              onClick={() => updateScoring("receptions", opt.value)}
              style={{
                flex: 1, padding: "6px 8px", fontSize: 11, fontWeight: 700, fontFamily: "inherit",
                background: active ? theme.colors.brand.orange : theme.colors.extended.modalBg,
                color: active ? "#fff" : "#b0bec5",
                border: `1px solid ${theme.colors.border.default}`,
                borderRadius: i === 0 ? "6px 0 0 6px" : i === 2 ? "0 6px 6px 0" : "0",
                borderLeft: i > 0 ? "none" : `1px solid ${theme.colors.border.default}`,
                transition: "all 0.15s ease",
              }}
            >{opt.label}</button>
          );
        })}
      </div>

      {/* Platform Presets */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        {basePresets.map(p => (
          <button key={p.name} className="btn" onClick={() => applyBasePreset(p)}
            style={{
              background: activePreset === p.name ? theme.colors.brand.orange : theme.colors.extended.modalBg,
              color: activePreset === p.name ? "#fff" : "#b0bec5",
              padding: "4px 10px", borderRadius: 4,
              fontSize: 10, fontWeight: 600, border: `1px solid ${theme.colors.border.default}`, fontFamily: "inherit",
            }}
            onMouseEnter={e => { if (activePreset !== p.name) { e.currentTarget.style.background = theme.colors.bg.elevated; e.currentTarget.style.color = theme.colors.text.primary; } }}
            onMouseLeave={e => { if (activePreset !== p.name) { e.currentTarget.style.background = theme.colors.extended.modalBg; e.currentTarget.style.color = "#b0bec5"; } }}
          >{p.name}</button>
        ))}
      </div>

      {/* Scoring Modifiers */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {scoringModifiers.map(mod => {
          const isActive = activeModifiers.has(mod.name);
          return (
            <button key={mod.name} className="btn" onClick={() => toggleModifier(mod)}
              style={{
                background: isActive ? theme.helpers.brandBg(0.15) : theme.colors.extended.modalBg,
                color: isActive ? theme.colors.brand.orange : "#b0bec5",
                padding: "3px 10px", borderRadius: 12,
                fontSize: 10, fontWeight: 600, fontFamily: "inherit",
                border: isActive ? `1px solid ${theme.colors.brand.orange}` : `1px solid ${theme.colors.border.default}`,
                transition: "all 0.15s ease",
              }}
            >{isActive ? "✓ " : ""}{mod.name}</button>
          );
        })}
      </div>

      {/* Scoring Sub-Tabs */}
      {(() => {
        const subTabs = [
          { label: "Passing", groups: [scoringGroups[0]] },
          { label: "Rushing", groups: [scoringGroups[1]] },
          { label: "Receiving", groups: [scoringGroups[2]] },
          { label: "Kicking", groups: [scoringGroups[4]] },
          { label: "Misc", groups: [scoringGroups[3], scoringGroups[5], scoringGroups[6]] },
        ];
        const activeSubGroups = subTabs[scoringSubTab]?.groups || [];
        return (
          <>
            <div style={{ display: "flex", gap: 0, marginBottom: 12, borderBottom: `1px solid ${theme.colors.border.default}` }}>
              {subTabs.map((st, i) => (
                <button key={st.label} onClick={() => setScoringSubTab(i)} style={{
                  flex: 1, padding: "7px 0", fontSize: 10, fontWeight: 700, letterSpacing: "0.5px",
                  fontFamily: theme.fonts.display, cursor: "pointer", border: "none",
                  background: "transparent",
                  color: scoringSubTab === i ? theme.colors.brand.orange : theme.colors.text.muted,
                  borderBottom: scoringSubTab === i ? `2px solid ${theme.colors.brand.orange}` : "2px solid transparent",
                  transition: "all 0.15s ease",
                }}>{st.label}</button>
              ))}
            </div>

            {/* Active sub-tab fields */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {activeSubGroups.map(group => (
                <div key={group.title} style={{ padding: "10px 12px", background: theme.colors.extended.modalBg, borderRadius: 8, border: `1px solid ${theme.colors.bg.elevated}` }}>
                  <div style={{
                    fontSize: 10, fontWeight: 700, color: group.color, letterSpacing: "1px",
                    marginBottom: 8, paddingBottom: 4, borderBottom: `1px solid ${group.color}33`,
                  }}>{group.title}</div>
                  {group.fields.map(([key, label, step]) => scoringInput(key, label, step))}
                </div>
              ))}
            </div>

            {/* Yardage Bonuses — visible on Passing/Rushing/Receiving tabs */}
            {scoringSubTab <= 2 && (
              <div style={{ marginTop: 10, padding: "10px 12px", background: theme.colors.extended.modalBg, borderRadius: 8, border: `1px solid ${theme.colors.bg.elevated}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: theme.colors.brand.orange, letterSpacing: "1px", marginBottom: 8, paddingBottom: 4, borderBottom: `1px solid ${theme.colors.brand.orange}33` }}>YARDAGE BONUSES</div>
                {bonusRows.map(row => {
                  const thresh = draft.scoring[row.threshKey] || 0;
                  const pts = draft.scoring[row.ptsKey] || 0;
                  const active = thresh > 0 && pts > 0;
                  const toggle = () => {
                    if (active) {
                      updateScoring(row.threshKey, 0);
                      updateScoring(row.ptsKey, 0);
                    } else {
                      updateScoring(row.threshKey, thresh || row.defaultThresh);
                      updateScoring(row.ptsKey, pts || row.defaultPts);
                    }
                  };
                  const stepBtn = (key, val, delta, min) => (
                    <button onClick={() => updateScoring(key, Math.max(min, val + delta))}
                      style={{ width: 28, minWidth: 28, height: 28, minHeight: 28, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", background: theme.colors.bg.elevated, border: `1px solid ${theme.colors.border.default}`, borderRadius: 4, color: theme.colors.text.secondary, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", lineHeight: 1, flexShrink: 0 }}
                    >{delta > 0 ? "+" : "\u2212"}</button>
                  );
                  return (
                    <div key={row.label} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <button onClick={toggle}
                        style={{ width: 40, minWidth: 40, height: 28, minHeight: 28, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 14, border: "none", cursor: "pointer", flexShrink: 0, transition: "background 0.15s ease", background: active ? row.color : theme.colors.bg.elevated }}>
                        <span style={{ width: 16, height: 16, borderRadius: "50%", background: active ? "#fff" : theme.colors.text.faint, transition: "transform 0.15s ease", transform: active ? "translateX(6px)" : "translateX(-6px)" }} />
                      </button>
                      <span style={{ width: 32, fontSize: 11, fontWeight: 600, color: active ? row.color : theme.colors.text.faint, flexShrink: 0 }}>{row.label}</span>
                      {active ? (
                        <>
                          {stepBtn(row.threshKey, thresh, -row.threshStep, row.threshStep)}
                          <input type="number" value={thresh} onChange={e => updateScoring(row.threshKey, Math.max(0, parseInt(e.target.value) || 0))}
                            style={{ width: 48, height: 28, background: theme.colors.extended.modalBg, border: `1px solid ${theme.colors.border.default}`, borderRadius: 4, padding: "0 4px", color: theme.colors.text.primary, fontSize: 12, textAlign: "center", fontFamily: theme.fonts.body }} />
                          {stepBtn(row.threshKey, thresh, row.threshStep, row.threshStep)}
                          <span style={{ fontSize: 9, color: theme.colors.text.muted, flexShrink: 0 }}>yds</span>
                          {stepBtn(row.ptsKey, pts, -1, 1)}
                          <input type="number" value={pts} onChange={e => updateScoring(row.ptsKey, Math.max(0, parseInt(e.target.value) || 0))}
                            style={{ width: 36, height: 28, background: theme.colors.extended.modalBg, border: `1px solid ${theme.colors.border.default}`, borderRadius: 4, padding: "0 4px", color: "#f59e0b", fontSize: 12, fontWeight: 700, textAlign: "center", fontFamily: theme.fonts.body }} />
                          {stepBtn(row.ptsKey, pts, 1, 1)}
                          <span style={{ fontSize: 9, color: theme.colors.text.muted, flexShrink: 0 }}>pts</span>
                        </>
                      ) : (
                        <span style={{ fontSize: 10, color: theme.colors.text.faint }}>Off</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Reset to Defaults */}
            <div style={{ marginTop: 12, textAlign: "center" }}>
              <button className="btn" onClick={() => { setDraft(prev => ({ ...prev, scoring: { ...DEFAULT_SCORING } })); setActiveFormatPreset(null); }}
                style={{ background: "none", color: theme.colors.text.secondary, fontSize: 10, fontWeight: 600, padding: "4px 12px", border: `1px solid ${theme.colors.border.default}`, borderRadius: 4, fontFamily: "inherit" }}
                onMouseEnter={e => e.currentTarget.style.color = theme.colors.status.danger}
                onMouseLeave={e => e.currentTarget.style.color = theme.colors.text.secondary}
              >Reset to Defaults</button>
            </div>
          </>
        );
      })()}
    </div>
  </>);

  const renderRosterTab = () => (<>
    <div className="sm-roster" style={{ marginBottom: 20 }}>
      {/* Header with total */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: theme.colors.text.secondary, letterSpacing: "1px" }}>ROSTER SLOTS</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: theme.colors.brand.orange }}>{totalSlots} total</span>
      </div>

      {/* Position rows with color dot, label, +/- buttons */}
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {posRows.map(({ pos, label, color }) => {
          const count = draft.positions[pos] || 0;
          return (
            <div key={pos} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "6px 10px",
              background: theme.colors.extended.modalBg, borderRadius: 8, border: `1px solid ${theme.colors.bg.elevated}`,
              height: 40, boxSizing: "border-box",
            }}>
              {/* Color dot */}
              <span style={{
                width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0,
              }} />
              {/* Position tag */}
              <span style={{
                fontSize: 11, fontWeight: 700, color: color, minWidth: 38,
                letterSpacing: "0.5px", flexShrink: 0,
              }}>{pos}</span>
              {/* Label */}
              <span style={{ flex: 1, fontSize: 11, color: theme.colors.text.secondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
              {/* +/- controls */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <button style={counterBtn} onClick={() => updatePos(pos, count - 1)}
                  onMouseEnter={e => e.currentTarget.style.background = theme.colors.border.default}
                  onMouseLeave={e => e.currentTarget.style.background = theme.colors.bg.elevated}
                >−</button>
                <span style={{
                  fontSize: 15, fontWeight: 800, color: count > 0 ? theme.colors.text.primary : theme.colors.text.faint,
                  minWidth: 20, textAlign: "center", fontFamily: theme.fonts.display,
                }}>{count}</span>
                <button style={counterBtn} onClick={() => updatePos(pos, count + 1)}
                  onMouseEnter={e => e.currentTarget.style.background = theme.colors.border.default}
                  onMouseLeave={e => e.currentTarget.style.background = theme.colors.bg.elevated}
                >+</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Visual roster preview — colored chips */}
      <RosterPreview positions={draft.positions} />
    </div>

    {/* Warning if roster changes */}
    <div className="sm-warning" style={{ padding: "6px 10px", background: "rgba(251, 191, 36, 0.1)", borderRadius: 6, border: "1px solid rgba(251, 191, 36, 0.2)", marginBottom: 16 }}>
      <span style={{ fontSize: 10, color: theme.colors.brand.accent }}>⚠ Changing settings will clear your current roster build.</span>
    </div>
  </>);

  return (<>
    <div className="modal-overlay" onClick={onClose} style={{ alignItems: "flex-start" }}>
      <div className="modal settings-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, maxHeight: "90vh", overflowY: "auto", marginTop: "5vh" }}>
        <div className="sm-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 0 }}>
          <h3 style={{ fontFamily: theme.fonts.display, fontWeight: 800, fontSize: 20 }}>⚙ League Settings</h3>
          <button className="btn" onClick={onClose} style={{ background: "none", color: theme.colors.text.muted, fontSize: 20, padding: 4, fontFamily: "inherit" }}>×</button>
        </div>

        {/* ──── THE CREATE STEPPER, STEPS 2-4 ────
            One pass to done. Every step is answered by defaults already, so
            Next · Next · Done is a complete league — the stepper guides, it
            must not nag. There is no "More options" link because there are no
            more options: the options ARE the flow.

            Steps 3 and 4 mount the SAME renderers the Scoring and Roster tabs
            do. A returning editor never sees any of this — see the tab bar in
            the other branch. */}
        {inWizard ? (
          <div style={{ marginTop: 16 }}>
            {/* Four segments, filled to here. The bar is the at-a-glance
                version of the sentence under it; the sentence is the one a
                screen reader gets. */}
            <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
              {CREATE_STEPS.map((s, i) => (
                <div key={s.title} style={{
                  flex: 1, height: 3, borderRadius: 2,
                  background: i < createStep ? theme.colors.brand.orange : theme.colors.bg.elevated,
                  transition: "background 0.2s ease",
                }} />
              ))}
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: theme.colors.text.secondary, letterSpacing: "0.5px" }}>
              Step {createStep} of {CREATE_STEP_COUNT} · {stepMeta.title}
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, color: theme.colors.text.primary, fontFamily: theme.fonts.display, marginTop: 2 }}>
              {activeSaveName || "Your league"}
            </div>
            <div style={{ fontSize: 11, color: theme.colors.text.secondary, marginTop: 4, marginBottom: 14, lineHeight: 1.5 }}>
              {stepMeta.blurb}
            </div>

            {createStep === 2 && (
              <div style={{
                background: theme.colors.extended.modalBg, border: `1px solid ${theme.colors.border.default}`,
                borderRadius: 8, padding: 14, marginBottom: 14,
              }}>
                <EssentialsControls
                  draft={draft} setDraft={setDraft}
                  onKnob={() => setActiveFormatPreset(null)}
                  keeperValue={isKeeperLeague}
                  onKeeperChange={activeSaveName ? setIsKeeperLeague : null}
                />
                <RosterPreview positions={draft.positions} compact />
              </div>
            )}
            {createStep === 3 && renderScoringTab()}
            {createStep === 4 && renderRosterTab()}

            {/* Word-for-word the actions row's notice. Two phrasings of one
                fact is how a user ends up believing the milder one. */}
            {mockDraftLive && (
              <div style={{ fontSize: 11, color: theme.colors.brand.accent, lineHeight: 1.4, marginBottom: 10 }}>
                {MID_DRAFT_UNSAVED_NOTICE}
              </div>
            )}

            <div className="sm-actions" style={{
              display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
              position: "sticky", bottom: 0, background: theme.colors.extended.modalBgDeep,
              paddingTop: 8, borderTop: `1px solid ${theme.colors.bg.elevated}`, marginTop: 4,
            }}>
              <button className="btn" onClick={goBackStep} style={{
                background: theme.colors.border.default, color: theme.colors.text.secondary,
                padding: "9px 16px", borderRadius: 6, fontSize: 13, fontWeight: 600,
                fontFamily: "inherit", border: "none", cursor: "pointer",
              }}>← Back</button>
              <div style={{ flex: 1 }} />
              {createStep < CREATE_STEP_COUNT ? (
                <button className="btn" onClick={() => setCreateStep(s => s + 1)} style={{
                  background: theme.colors.brand.gradientBtn, color: "#fff",
                  padding: "9px 22px", borderRadius: 6, fontSize: 13, fontWeight: 700,
                  fontFamily: "inherit", border: "none", cursor: "pointer",
                }}>Next →</button>
              ) : (
                <button className="btn" onClick={finishCreate} style={{
                  background: theme.colors.brand.gradientBtn, color: "#fff",
                  padding: "9px 22px", borderRadius: 6, fontSize: 13, fontWeight: 700,
                  fontFamily: "inherit", border: "none", cursor: "pointer",
                }}>Done — take me to my league</button>
              )}
            </div>
          </div>
        ) : (<>

        {/* Tab Bar */}
        <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: `1px solid ${theme.colors.border.default}` }}>
          {["My Leagues", "Format", "Scoring", "Roster"].map((label, i) => (
            <button key={label} onClick={() => setSettingsTab(i)} style={{
              flex: 1, padding: "10px 0", fontSize: 11, fontWeight: 700, letterSpacing: "0.5px",
              fontFamily: theme.fonts.display, cursor: "pointer", border: "none",
              background: "transparent",
              color: settingsTab === i ? theme.colors.brand.orange : theme.colors.text.muted,
              borderBottom: settingsTab === i ? `2px solid ${theme.colors.brand.orange}` : "2px solid transparent",
              transition: "all 0.15s ease",
            }}>{label}</button>
          ))}
        </div>

        {/* ──── TAB 0: My Leagues ──── */}
        {settingsTab === 0 && <>
        {/* The same entry as the header dropdown's first item, at the top of
            the tab rather than only at the bottom. Someone who opened League
            Settings looking for "where do I make one" should not have to read
            past three import blocks to find out. Identical styling to the
            dropdown item so they read as one affordance in two places. */}
        <button className="btn" onClick={() => openCreate("")} disabled={mockDraftLive}
          style={{
            width: "100%", padding: "9px 14px", borderRadius: 6, marginBottom: 16,
            fontSize: 12, fontWeight: 700, fontFamily: "inherit",
            background: "transparent", color: theme.colors.brand.orange,
            border: `1px solid ${theme.colors.border.brand}`,
            cursor: mockDraftLive ? "not-allowed" : "pointer",
            opacity: mockDraftLive ? 0.5 : 1,
          }}
        >+ Create new league</button>

        {/* Saved Leagues */}
        <div className="sm-leagues" style={{ marginBottom: 20 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: theme.colors.text.secondary, letterSpacing: "1px", display: "block", marginBottom: 8 }}>SAVED LEAGUES</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              value={matchedLeague?.name ?? (activeFormatPreset ? PRESET_OPT + activeFormatPreset : "")}
              onChange={e => {
                const v = e.target.value;
                // Saved leagues resolve FIRST, so a saved league that happens to
                // be named like the option token can never be shadowed by a preset.
                const league = savedLeagues.find(l => l.name === v);
                if (league) { loadLeague(league); return; }
                if (v.startsWith(PRESET_OPT)) {
                  const preset = FORMAT_PRESETS.find(p => p.name === v.slice(PRESET_OPT.length));
                  if (preset) applyFormatPreset(preset);
                }
              }}
              style={{
                flex: 1, background: theme.colors.extended.modalBg, color: theme.colors.text.primary,
                border: `1px solid ${theme.colors.border.default}`, borderRadius: 6,
                padding: "7px 10px", fontSize: 12, fontWeight: 600, fontFamily: theme.fonts.body,
                cursor: "pointer", appearance: "auto",
              }}
            >
              {!matchedLeague && !activeFormatPreset && <option value="" disabled>— Select a league —</option>}
              {savedLeagues.map(league => (
                <option key={league.name} value={league.name}>{league.name}</option>
              ))}
              {/* The five format presets, in the selector rather than on the
                  Format tab. A preset is a whole league shape, so it belongs
                  next to the leagues — and the Format tab is then purely the
                  custom knobs (budget / teams / QB format). Same group, same
                  order, same derived subtitles as the header dropdown. */}
              <optgroup label="START FROM A PRESET">
                {FORMAT_PRESETS.map(preset => (
                  <option key={preset.name} value={PRESET_OPT + preset.name}>
                    {preset.name} — {presetSubtitle(preset)}
                  </option>
                ))}
              </optgroup>
            </select>
            {savedLeagues.length > 1 && matchedLeague && (
              <span onClick={() => deleteLeague(matchedLeague.name)}
                style={{ fontSize: 11, fontWeight: 600, color: theme.colors.status.danger, cursor: "pointer", whiteSpace: "nowrap" }}
                title="Delete this league"
              >Delete</span>
            )}
          </div>
          {activeSaveName ? (
            /* A named league is LIVE. There is nothing to save, so there is no
               save button — just the standing statement that it is saving, and
               the one action that still makes sense here: forking. */
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5, flexWrap: "wrap" }}>
              <span style={{
                fontSize: 10, fontWeight: 600,
                color: updatedFlash ? theme.colors.brand.accent : theme.colors.text.secondary,
              }}>
                {mockDraftLive
                  ? `Paused while a draft is live — changes are NOT saved`
                  : updatedFlash ? `✓ Saved to ${updatedFlash}` : `Auto-saving to ${activeSaveName}`}
              </span>
              <button className="btn" onClick={() => openCreate(suggestLeagueName(savedLeagues, `${activeSaveName} copy`))}
                disabled={mockDraftLive}
                style={{ background: "none", color: theme.colors.text.secondary, fontSize: 10, fontWeight: 600, padding: "2px 8px",
                  border: `1px solid ${theme.colors.border.default}`, borderRadius: 4, fontFamily: "inherit",
                  cursor: mockDraftLive ? "not-allowed" : "pointer", opacity: mockDraftLive ? 0.5 : 1 }}
                title="Fork this league into a new one"
              >Save a copy</button>
            </div>
          ) : (
            /* DEFAULT — quick play. Explicitly commit-based: these settings
               belong to no league, so nothing can auto-save them anywhere.
               Naming them is what makes them live, and that routes through the
               same create step as everything else. */
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, color: theme.colors.text.secondary, fontWeight: 400 }}>
                {hasUnsavedChanges ? "Not saved to a league" : "Select a league to load its settings"}
              </span>
              <button className="btn" onClick={() => openCreate("")} disabled={mockDraftLive}
                style={{ background: "none", color: theme.colors.brand.orange, fontSize: 10, fontWeight: 700, padding: "2px 8px",
                  border: `1px solid ${theme.colors.border.brand}`, borderRadius: 4, fontFamily: "inherit",
                  cursor: mockDraftLive ? "not-allowed" : "pointer", opacity: mockDraftLive ? 0.5 : 1 }}
              >Save as a league…</button>
            </div>
          )}
          {matchedLeague && (() => {
            const s = draft.scoring;
            const ppr = s.receptions === 0 ? "Standard" : s.receptions === 0.5 ? "Half-PPR" : s.receptions === 1 ? "Full PPR" : `${s.receptions} PPR`;
            const p = draft.positions;
            const posParts = [];
            if (p.QB) posParts.push(p.QB > 1 ? `${p.QB}QB` : "QB");
            if (p.RB) posParts.push(p.RB > 1 ? `${p.RB}RB` : "RB");
            if (p.WR) posParts.push(p.WR > 1 ? `${p.WR}WR` : "WR");
            if (p.TE) posParts.push(p.TE > 1 ? `${p.TE}TE` : "TE");
            if (p.FLEX) posParts.push(p.FLEX > 1 ? `${p.FLEX}FLEX` : "FLEX");
            if (p.SFLEX) posParts.push(p.SFLEX > 1 ? `${p.SFLEX}SF` : "SF");
            if (p.K) posParts.push("K");
            if (p.DEF) posParts.push("DEF");
            if (p.BN) posParts.push(`${p.BN}BN`);
            return (
              <div style={{ fontSize: 10, color: theme.colors.text.faint, marginTop: 6, lineHeight: 1.5 }}>
                {ppr} · {s.passTD}PT Pass TD · {draft.teams} Teams · ${draft.budget} · {posParts.join("/")}
              </div>
            );
          })()}
          {/* Outside the matchedLeague block on purpose: this reads from
              draft.positions, so it stays correct for a freshly imported league
              and for hand-edited settings that no longer match a saved league. */}
          <RosterPreview positions={draft.positions} compact />
        </div>

        {/* Sleeper Import. The ref is where the create step's "or import from
            Sleeper / ESPN" hands off to — imports name and save themselves, so
            that path forwards here rather than duplicating anything. */}
        <div ref={importRef} style={{ marginBottom: 16 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: theme.colors.text.secondary, letterSpacing: "1px", display: "block", marginBottom: 8 }}>IMPORT FROM SLEEPER</span>
          {sleeperStep === "username" && (
            <>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="text"
                  value={sleeperUsername}
                  onChange={e => { setSleeperUsername(e.target.value); setSleeperError(null); }}
                  onKeyDown={e => e.key === "Enter" && !sleeperLoading && handleFindLeagues()}
                  placeholder="Sleeper username"
                  style={{
                    flex: 1, background: theme.colors.extended.modalBg, border: `1px solid ${theme.colors.border.default}`,
                    borderRadius: 6, padding: "7px 10px", color: theme.colors.text.primary, fontSize: 12,
                    fontFamily: theme.fonts.body,
                  }}
                />
                <button
                  className="btn"
                  onClick={handleFindLeagues}
                  disabled={sleeperLoading || !sleeperUsername.trim()}
                  style={{
                    padding: "7px 14px", borderRadius: 6, fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                    background: sleeperLoading || !sleeperUsername.trim() ? theme.colors.bg.elevated : theme.colors.brand.deep,
                    color: sleeperLoading || !sleeperUsername.trim() ? theme.colors.text.faint : "#fff",
                    border: "none", cursor: sleeperLoading || !sleeperUsername.trim() ? "default" : "pointer",
                    whiteSpace: "nowrap", transition: "all 0.15s ease",
                  }}
                >{sleeperLoading ? "Finding..." : "Find Leagues"}</button>
              </div>
              {sleeperError && (
                <div style={{ marginTop: 5, fontSize: 11, fontWeight: 600, color: theme.colors.status.danger }}>{sleeperError}</div>
              )}
            </>
          )}
          {sleeperStep === "picking" && (
            <>
              <div style={{ fontSize: 10, color: theme.colors.text.faint, marginBottom: 6 }}>
                {sleeperLeagues.length === 0
                  ? "No NFL leagues found for this user in 2025."
                  : `${sleeperLeagues.length} league${sleeperLeagues.length !== 1 ? "s" : ""} found · tap to import`}
                {" · "}<span style={{ cursor: "pointer", color: theme.colors.text.secondary, textDecoration: "underline" }}
                  onClick={() => { setSleeperStep("username"); setSleeperError(null); }}>change user</span>
              </div>
              {sleeperLeagues.length > 0 && <div style={{ display: "flex", flexDirection: "column", gap: 1, borderRadius: 6, overflow: "hidden", border: `1px solid ${theme.colors.border.default}` }}>
                {sleeperLeagues.map((l, i) => {
                  const isLast = i === sleeperLeagues.length - 1;
                  const isImporting = sleeperImporting === l.league_id;
                  return (
                    <div key={l.league_id} style={{
                      display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
                      background: theme.colors.extended.modalBg,
                      borderBottom: isLast ? "none" : `1px solid ${theme.colors.bg.elevated}`,
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = theme.colors.bg.elevated}
                      onMouseLeave={e => e.currentTarget.style.background = theme.colors.extended.modalBg}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: theme.colors.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.name}</div>
                        <div style={{ fontSize: 10, color: theme.colors.text.secondary, marginTop: 1 }}>{sleeperLeagueFormat(l)}</div>
                      </div>
                      <button className="btn" onClick={() => handleImportLeague(l.league_id)} disabled={!!sleeperImporting}
                        style={{
                          padding: "4px 10px", borderRadius: 4, fontSize: 11, fontWeight: 700, fontFamily: "inherit",
                          background: isImporting ? theme.colors.bg.elevated : theme.colors.brand.deep,
                          color: isImporting ? theme.colors.text.faint : "#fff",
                          border: "none", cursor: sleeperImporting ? "default" : "pointer", flexShrink: 0,
                        }}
                      >{isImporting ? "Importing..." : "Import"}</button>
                    </div>
                  );
                })}
              </div>}
              {sleeperError && (
                <div style={{ marginTop: 5, fontSize: 11, fontWeight: 600, color: theme.colors.status.danger }}>{sleeperError}</div>
              )}
            </>
          )}
          {/* Outside both step blocks so it is on screen at the username step
              AND at the moment the user clicks Import. Sleeper-only on purpose:
              the Yahoo path carries no auction history and contributes nothing,
              so showing it there would claim a thing that doesn't happen. */}
          <div style={{ marginTop: 8, fontSize: 10, lineHeight: 1.5, color: theme.colors.text.faint }}>
            {CONTRIBUTION_DISCLOSURE}
          </div>
        </div>

        {/* Yahoo Import */}
        <div style={{ marginBottom: 16 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: theme.colors.text.secondary, letterSpacing: "1px", display: "block", marginBottom: 8 }}>IMPORT FROM YAHOO</span>
          {/* YAHOO IS DISABLED HERE, NOT REMOVED — the second entry point after
              the Almanac platform picker, and the one that could still open the
              OAuth popup. "Connect Yahoo Account" is replaced by the same
              non-interactive line the Almanac card and the Almanac Yahoo flow
              show, from one shared constant so all three read identically.

              Everything below (waiting / picking / importYahooLeague) is left
              exactly as it was and simply cannot be reached while nothing moves
              yahooStep off "idle". Restoring access is deleting this block. */}
          {yahooStep === "idle" && (
            <>
              <div style={{ fontSize: 11, lineHeight: 1.55, color: theme.colors.text.faint }}>
                <strong style={{ color: theme.colors.text.secondary }}>{YAHOO_UNAVAILABLE_LEAD}</strong>{" "}
                {YAHOO_UNAVAILABLE_BODY}
              </div>
              {yahooError && (
                <div style={{ marginTop: 5, fontSize: 11, fontWeight: 600, color: theme.colors.status.danger }}>{yahooError}</div>
              )}
            </>
          )}
          {yahooStep === "waiting" && (
            <div style={{ fontSize: 12, color: theme.colors.text.secondary, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ display: "inline-block", width: 12, height: 12, borderRadius: "50%", border: `2px solid ${theme.colors.brand.deep}`, borderTopColor: "transparent", animation: "spin 0.8s linear infinite" }} />
              Waiting for Yahoo login...
            </div>
          )}
          {yahooStep === "picking" && (
            <>
              <div style={{ fontSize: 10, color: theme.colors.text.faint, marginBottom: 6 }}>
                {yahooLeagues.length === 0
                  ? "No NFL auction leagues found."
                  : `${yahooLeagues.length} league${yahooLeagues.length !== 1 ? "s" : ""} found · tap to import`}
                {" · "}<span style={{ cursor: "pointer", color: theme.colors.text.secondary, textDecoration: "underline" }}
                  onClick={() => { setYahooStep("idle"); setYahooError(null); }}>reconnect</span>
              </div>
              {yahooLeagues.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 1, borderRadius: 6, overflow: "hidden", border: `1px solid ${theme.colors.border.default}` }}>
                  {yahooLeagues.map((l, i) => {
                    const isLast = i === yahooLeagues.length - 1;
                    return (
                      <div key={l.league_key} style={{
                        display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
                        background: theme.colors.extended.modalBg,
                        borderBottom: isLast ? "none" : `1px solid ${theme.colors.bg.elevated}`,
                        cursor: "pointer",
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = theme.colors.bg.elevated}
                        onMouseLeave={e => e.currentTarget.style.background = theme.colors.extended.modalBg}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: theme.colors.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.name}</div>
                          <div style={{ fontSize: 10, color: theme.colors.text.secondary, marginTop: 1 }}>
                            {l.season} · {l.num_teams}-team · {l.is_auction_draft === '1' || l.is_auction_draft === 1 ? "Auction" : "Snake"} · {l.scoring_type}
                          </div>
                        </div>
                        <button className="btn" onClick={() => importYahooLeague(l)}
                          style={{
                            padding: "4px 10px", borderRadius: 4, fontSize: 11, fontWeight: 700, fontFamily: "inherit",
                            background: theme.colors.brand.deep, color: "#fff",
                            border: "none", cursor: "pointer", flexShrink: 0,
                          }}
                        >Import</button>
                      </div>
                    );
                  })}
                </div>
              )}
              {yahooError && (
                <div style={{ marginTop: 5, fontSize: 11, fontWeight: 600, color: theme.colors.status.danger }}>{yahooError}</div>
              )}
            </>
          )}
        </div>

        {/* ESPN Import — public by league ID, private by league ID + cookies */}
        <div style={{ marginBottom: 16 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: theme.colors.text.secondary, letterSpacing: "1px", display: "block", marginBottom: 8 }}>IMPORT FROM ESPN</span>

          {espnStep === "input" && (
            <>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="text"
                  value={espnLeagueId}
                  onChange={e => { setEspnLeagueId(e.target.value); setEspnError(null); }}
                  onKeyDown={e => e.key === "Enter" && handleEspnImport()}
                  placeholder="ESPN league ID"
                  inputMode="numeric"
                  style={{
                    flex: 1, background: theme.colors.extended.modalBg, border: `1px solid ${theme.colors.border.default}`,
                    borderRadius: 6, padding: "7px 10px", color: theme.colors.text.primary, fontSize: 12,
                    fontFamily: theme.fonts.body,
                  }}
                />
                <button
                  className="btn"
                  onClick={handleEspnImport}
                  disabled={!espnLeagueId.trim()}
                  style={{
                    padding: "7px 14px", borderRadius: 6, fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                    background: !espnLeagueId.trim() ? theme.colors.bg.elevated : theme.colors.brand.deep,
                    color: !espnLeagueId.trim() ? theme.colors.text.faint : "#fff",
                    border: "none", cursor: !espnLeagueId.trim() ? "default" : "pointer",
                    whiteSpace: "nowrap", transition: "all 0.15s ease",
                  }}
                >Import</button>
              </div>
              <div style={{ marginTop: 5, fontSize: 10, color: theme.colors.text.faint, lineHeight: 1.5 }}>
                The number after <code>leagueId=</code> in your league's URL.
              </div>
              {/* The taxonomy branch, rendered where the user actually is. A
                  failed walk returns to this step so the ID is still editable.
                  sentCreds is the attempt's own record of whether cookies went
                  out, so "those cookies didn't work" can't be shown to someone
                  who never sent any. */}
              {espnError && espnResult && (() => {
                const copy = espnErrorCopy(espnResult, espnResult.sentCreds);
                return (
                  <div style={{ marginTop: 6, fontSize: 11, lineHeight: 1.5, color: theme.colors.status.danger }}>
                    <div style={{ fontWeight: 700 }}>{copy.title}</div>
                    <div style={{ fontWeight: 400, color: theme.colors.text.secondary, marginTop: 2 }}>{copy.body}</div>
                  </div>
                );
              })()}

              {/* The manual way in, for someone who already knows. The auth
                  error reveals the same block automatically, which is the path
                  most people take. */}
              {!espnShowCreds && (
                <div
                  onClick={() => setEspnShowCreds(true)}
                  style={{ marginTop: 6, fontSize: 10, color: theme.colors.brand.orange, cursor: "pointer", fontWeight: 600 }}
                >My league is private →</div>
              )}

              {espnShowCreds && <EspnCredentialFields
                s2={espnS2} setS2={setEspnS2}
                swid={espnSwid} setSwid={setEspnSwid}
                onHide={() => { clearEspnCreds(); setEspnShowCreds(false); }}
              />}
            </>
          )}

          {espnStep === "fetching" && (
            <div style={{ fontSize: 12, color: theme.colors.text.secondary, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ display: "inline-block", width: 12, height: 12, borderRadius: "50%", border: `2px solid ${theme.colors.brand.deep}`, borderTopColor: "transparent", animation: "spin 0.8s linear infinite" }} />
              {espnProgress || "Walking back through seasons..."}
            </div>
          )}

          {espnStep === "summary" && espnResult && (() => {
            const seasons = espnResult.seasons ?? [];
            const years = seasons.map(s => s.season).filter(y => y != null).sort((a, b) => a - b);
            const range = years.length
              ? (years.length === 1 ? String(years[0]) : `${years[0]}–${years[years.length - 1]}`)
              : "no seasons";
            const auctionCount = seasons.filter(s => s.is_auction_draft).length;
            const totalPicks = seasons.reduce((n, s) => n + (s.picks?.length ?? 0), 0);
            const dropped = seasons.reduce((n, s) => n + (s.droppedPicks ?? 0), 0);
            // The filter note rides with the parser notes rather than in its
            // own box — a season contributing no prices is the same KIND of
            // caveat as a dropped pick, and splitting them would let one be
            // read without the other. Same call the save makes, so what the
            // user reads here is what actually gets contributed.
            const { filteredNote } = buildEspnAuctionHistory(seasons);
            const notes = [...(espnResult.notes ?? []), ...(filteredNote ? [filteredNote] : [])];
            const banner = espnResult.error ? espnErrorCopy(espnResult, espnResult.sentCreds) : null;
            return (
              <>
                <div style={{ fontSize: 12, color: theme.colors.text.primary, lineHeight: 1.6 }}>
                  <strong>{seasons.length}</strong> season{seasons.length !== 1 ? "s" : ""} ({range}) ·{" "}
                  {auctionCount} auction · {totalPicks} pick{totalPicks !== 1 ? "s" : ""}
                  {dropped > 0 && <> · <span style={{ color: theme.colors.status.warning }}>{dropped} dropped</span></>}
                </div>

                {/* Why the range starts and ends where it does. Without it, a
                    league missing its current season reads as lost data rather
                    than a season ESPN hasn't opened yet. */}
                <div style={{ marginTop: 6, fontSize: 10, color: theme.colors.text.faint, lineHeight: 1.6 }}>
                  {espnResult.advancedFrom != null
                    ? <div>Started at {espnResult.startedAt} — ESPN has no {espnResult.advancedFrom} season yet.</div>
                    : espnResult.startedAt != null && <div>Started at {espnResult.startedAt}.</div>}
                  <div>{describeEspnStop(espnResult)}</div>
                </div>

                {/* Stopped on something retryable but kept what it collected.
                    Continuing short and resuming are both valid, so both are
                    offered rather than one being assumed. */}
                {banner && (
                  <div style={{ marginTop: 6, fontSize: 11, lineHeight: 1.5 }}>
                    <div style={{ fontWeight: 700, color: theme.colors.status.danger }}>{banner.title}</div>
                    <div style={{ color: theme.colors.text.secondary, marginTop: 2 }}>{banner.body}</div>
                    {/* A 401 MID-WALK is a cookie that expired partway down —
                        normal on a 20-season league. The fields come with the
                        prompt so re-pasting and resuming is one screen, not a
                        trip back to the start that would discard the seasons
                        already collected. Resume reads the credential state
                        live, so whatever is pasted here is what goes out. */}
                    {espnResult.error === "auth" && (
                      <EspnCredentialFields
                        s2={espnS2} setS2={setEspnS2}
                        swid={espnSwid} setSwid={setEspnSwid}
                        onHide={clearEspnCreds}
                      />
                    )}
                    {banner.retryable && (
                      <span
                        onClick={handleEspnResume}
                        style={{ display: "inline-block", marginTop: 6, cursor: "pointer", color: theme.colors.brand.orange, fontWeight: 600, textDecoration: "underline" }}
                      >Resume from {espnResult.stoppedAt}</span>
                    )}
                  </div>
                )}

                {/* THE NOTES ARE NOT DEBUG OUTPUT. Dropped picks, FLEX
                    generalization, scoring-id collisions and missing bonuses
                    each change what the import MEANS — and here they change
                    what gets contributed. Burying them would make a lossy
                    import look clean. */}
                {notes.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: theme.colors.text.secondary, letterSpacing: "0.5px", marginBottom: 4 }}>
                      {notes.length} IMPORT NOTE{notes.length !== 1 ? "S" : ""}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 160, overflowY: "auto" }}>
                      {notes.map((n, i) => (
                        <div key={i} style={{
                          fontSize: 10, color: theme.colors.text.secondary, lineHeight: 1.5,
                          background: theme.colors.extended.modalBg,
                          border: `1px solid ${theme.colors.border.default}`,
                          borderRadius: 4, padding: "6px 8px",
                        }}>{n}</div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 8, display: "flex", gap: 6, alignItems: "center" }}>
                  <button className="btn" onClick={handleEspnSave}
                    style={{
                      padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                      background: theme.colors.brand.deep, color: "#fff", border: "none", cursor: "pointer",
                    }}
                  >Use this league</button>
                  <span
                    onClick={() => { setEspnCarried([]); setEspnResult(null); setEspnStep("input"); clearEspnCreds(); }}
                    style={{ fontSize: 10, cursor: "pointer", color: theme.colors.text.secondary, textDecoration: "underline" }}
                  >different league</span>
                </div>
              </>
            );
          })()}

          {/* Same placement rule as the Sleeper block: outside every step, so
              it is on screen at the input AND at the moment the user commits.
              Shown here because this path DOES contribute — ESPN carries real
              auction history, which is exactly what the Yahoo path lacks. */}
          <div style={{ marginTop: 8, fontSize: 10, lineHeight: 1.5, color: theme.colors.text.faint }}>
            {CONTRIBUTION_DISCLOSURE}
          </div>
        </div>

        {/* Build by hand — the escape hatch for users with nothing to import.
            It used to be a line of small orange text, which is exactly the
            problem: the one path for a user with no Sleeper or ESPN league was
            the least visible thing on the tab. It is a real button now, and it
            opens the same create step as "+ Create new league" in the header,
            so there is ONE way to make a league by hand rather than two that
            disagree about when it gets saved. */}
        <div style={{ marginBottom: 16 }}>
          <button className="btn" onClick={openCreate} disabled={mockDraftLive}
            style={{
              width: "100%", padding: "9px 14px", borderRadius: 6,
              fontSize: 12, fontWeight: 700, fontFamily: "inherit",
              background: "transparent", color: theme.colors.brand.orange,
              border: `1px solid ${theme.colors.border.brand}`,
              cursor: mockDraftLive ? "not-allowed" : "pointer",
              opacity: mockDraftLive ? 0.5 : 1,
            }}
          >+ Create new league</button>
          <div style={{ fontSize: 10, color: theme.colors.text.faint, marginTop: 5, lineHeight: 1.5 }}>
            No Sleeper or ESPN league? Name yours and set it up by hand — it saves right away.
          </div>
        </div>

        </>}

        {/* ──── TAB 1: Format ──── */}
        {settingsTab === 1 && <>
        {/* The five FORMAT PRESETS used to lead this tab. They now live in
            both league selectors (this modal's SAVED LEAGUES dropdown and the
            header league dropdown), under a START FROM A PRESET group: a preset
            is a whole league shape, so it belongs where you pick a league, not
            where you turn one knob. What is left here is the custom knobs —
            budget, teams, QB format — which is all the Format tab ever was for
            a user who did not want a canned format. */}
        <EssentialsControls
          draft={draft} setDraft={setDraft}
          onKnob={() => setActiveFormatPreset(null)}
          keeperValue={isKeeperLeague}
          onKeeperChange={activeSaveName ? setIsKeeperLeague : null}
        />
        </>}

        {/* ──── TAB 2: Scoring ──── (renderScoringTab — also step 3 of create) */}
        {settingsTab === 2 && renderScoringTab()}

        {/* ──── TAB 3: Roster ──── (renderRosterTab — also step 4 of create) */}
        {settingsTab === 3 && renderRosterTab()}

        {/* Done / Apply */}
        <div className="sm-actions" style={{ display: "flex", gap: 8, position: "sticky", bottom: 0, background: theme.colors.extended.modalBgDeep, paddingTop: 8, borderTop: `1px solid ${theme.colors.bg.elevated}`, marginTop: 4, flexWrap: "wrap" }}>
          {/* THE MID-DRAFT NOTICE, and for a named league it says more than it
              used to. A disabled Apply used to carry the message on its own:
              the button was visibly dead, so nothing had been saved. With
              auto-save there is no button to look dead, and silence would read
              as "saved" — so it states outright that changes are not being
              kept. See the auto-save effect for why this is not optional. */}
          {mockDraftLive && (
            <div style={{
              width: "100%", fontSize: 11, color: theme.colors.brand.accent,
              fontFamily: theme.fonts.body, lineHeight: 1.4,
            }}>
              {activeSaveName
                ? MID_DRAFT_UNSAVED_NOTICE
                : "Settings are locked while a draft is in progress — finish or leave the draft to change them."}
            </div>
          )}
          {activeSaveName ? (
            /* A named league saves itself, so there is nothing left for a
               commit button to do. Done closes; that is the whole contract. */
            <button className="btn" onClick={onClose} style={{
              flex: 1, background: theme.colors.brand.gradientBtn, color: "#fff",
              padding: "10px 16px", borderRadius: 6, fontSize: 14, fontWeight: 700, fontFamily: "inherit",
              cursor: "pointer",
            }}>Done</button>
          ) : (<>
            {/* DEFAULT — unchanged. Nothing owns these settings, so applying
                them is a real, explicit act. */}
            <button className="btn" onClick={handleApply} disabled={mockDraftLive} style={{
              flex: 1, background: theme.colors.brand.gradientBtn, color: "#fff",
              padding: "10px 16px", borderRadius: 6, fontSize: 14, fontWeight: 700, fontFamily: "inherit",
              opacity: mockDraftLive ? 0.5 : 1, cursor: mockDraftLive ? "not-allowed" : "pointer",
            }}>Apply Settings</button>
            <button className="btn" onClick={onClose} style={{
              background: theme.colors.border.default, color: theme.colors.text.secondary, padding: "10px 16px", borderRadius: 6,
              fontSize: 14, fontWeight: 600, fontFamily: "inherit",
            }}>Cancel</button>
          </>)}
        </div>
        </>)}
      </div>
    </div>

    {/* ──── STEP 1 OF 4: THE NAME ────
        One decision on screen. It sits above the settings modal rather than
        replacing it, so pressing Next reveals step 2 underneath — the user
        sees where they arrived instead of a modal appearing from nowhere.

        Still commits immediately: the league exists from here, which is what
        makes X-ing out at step 3 lossless. What v3 adds is the step counter
        and the fact that Next now leads somewhere rather than finishing.

        Nothing here touches the DEFAULT pseudo-league or either import path;
        this is an additional way in, not a replacement for them. */}
    {createOpen && (
      <div onClick={cancelNameStep} style={{
        position: "fixed", inset: 0, background: theme.helpers.overlay(), zIndex: 200,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}>
        <div onClick={e => e.stopPropagation()} style={{
          background: theme.colors.extended.modalBgDeep, border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12,
          padding: 20, width: "100%", maxWidth: 340, boxShadow: `0 8px 32px ${theme.helpers.overlay(0.5)}`,
          animation: "slideIn 0.2s ease-out", boxSizing: "border-box",
        }}>
          {/* Same bar, same sentence as steps 2-4 — the name step is part of
              the flow, not a dialog that happens before it. */}
          <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
            {CREATE_STEPS.map((s, i) => (
              <div key={s.title} style={{
                flex: 1, height: 3, borderRadius: 2,
                background: i < 1 ? theme.colors.brand.orange : theme.colors.bg.elevated,
              }} />
            ))}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.colors.text.secondary, letterSpacing: "0.5px" }}>
            Step 1 of {CREATE_STEP_COUNT} · Name
          </div>
          <div style={{ fontSize: 15, fontWeight: 800, color: theme.colors.text.primary, fontFamily: theme.fonts.display, marginTop: 2 }}>
            Name your league
          </div>
          <div style={{ fontSize: 11, color: theme.colors.text.secondary, marginTop: 4, marginBottom: 12, lineHeight: 1.5 }}>
            {createdName
              ? "Already saved. Change it here if you like, or carry on."
              : "It saves right away — three quick steps after this."}
          </div>
          <input type="text" value={createName}
            onChange={e => { setCreateName(e.target.value); setCreateNotice(null); }}
            onKeyDown={e => { if (e.key === "Enter") handleNameStepNext(); }}
            placeholder="e.g. Smithtown Dynasty" autoFocus maxLength={40}
            style={{
              width: "100%", background: theme.colors.extended.modalBg,
              border: `1px solid ${createNotice ? theme.colors.status.danger : theme.colors.border.default}`,
              borderRadius: 6, padding: "9px 12px", color: theme.colors.text.primary,
              fontSize: 13, fontFamily: theme.fonts.body, boxSizing: "border-box",
            }} />
          {/* A collision refills the field with the suggested name; the user
              still has to press Next, so nothing is renamed silently. */}
          {createNotice && (
            <div style={{ fontSize: 11, fontWeight: 600, color: theme.colors.status.danger, marginTop: 6, lineHeight: 1.4 }}>
              {createNotice}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="btn" onClick={handleNameStepNext} disabled={!createName.trim() || mockDraftLive}
              style={{
                flex: 1, background: createName.trim() && !mockDraftLive ? theme.colors.brand.gradientBtn : theme.colors.bg.elevated,
                color: createName.trim() && !mockDraftLive ? "#fff" : theme.colors.text.faint,
                padding: "9px 14px", borderRadius: 6, fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                border: "none", cursor: createName.trim() && !mockDraftLive ? "pointer" : "default",
              }}>Next →</button>
            <button className="btn" onClick={cancelNameStep}
              style={{
                background: theme.colors.border.default, color: theme.colors.text.secondary,
                padding: "9px 14px", borderRadius: 6, fontSize: 13, fontWeight: 600,
                fontFamily: "inherit", border: "none",
              }}>Cancel</button>
          </div>
          {/* The secondary path, on the SAME step — someone who does have a
              Sleeper or ESPN league should never have to name anything. Hidden
              on a revisit: they already made one, and an import would strand
              the league they are halfway through setting up. */}
          {!createdName && (<>
            <div style={{ borderTop: `1px solid ${theme.colors.border.default}`, margin: "14px 0 10px" }} />
            <span onClick={goToImport} style={{
              fontSize: 11, fontWeight: 600, color: theme.colors.brand.orange, cursor: "pointer",
            }}>or import from Sleeper / ESPN →</span>
          </>)}
        </div>
      </div>
    )}

  </>);
});

export default SettingsModal;
